import { inventoryStepOverSizeId } from '../janus-engine/inventory/step-over.js';
import { resolveObjectVitality } from '../janus-engine/inventory/object-vitality.js';
import { dedicatedPropSpace, inventoryMeshDrawn, selectedPropMeshSpaceOrTier } from '../janus-engine/inventory/prop-mesh-space.js';
import { hydrateInventoryDefinitions, inventoryCatalog, inventoryDefinition } from '../janus-engine/inventory/definition.js';
import { readInventoryField, readInventoryRows } from '../janus-engine/compile/inventory-raw-readers.js';
import { patchEncounterTerrainRows, type EncounterTerrainMetadata } from '../janus-engine/compile/from-base-encounter-terrain';
import { floorFallSavePlan, savedFloorInventory, savedFloorVitality, type FloorFallSave } from '../janus-engine/inventory/floor-save-plan.js';
import { planLatchedObjectMove } from '../janus-engine/movement/compound-drag.js';
import { validCreatureFollowers, type CreatureFollower } from '../janus-engine/movement/creature-followers.js';
import { latchedCreatures } from '../janus-engine/movement/latch-link.js';
import { encounterShapeValue } from '../janus-engine/inventory/mesh-scale.js';
import { inventorySceneryShell, inventoryAuthoredSizeId, inventoryEncounterLink, inventoryEquipmentSlot, inventoryFixtureFlags, inventoryPropName } from '../janus-engine/inventory/solid-obstacle.js';
import { refuseUnported } from '../janus-engine/unported';
import { HAND_DROP_SPLIT_SCHEMA, handDropSnapshot, looseFloorRow, type HandDropSnapshot } from '../janus-engine/geometry/hand-drop';
import { getProximityEntity } from '../shared/proximity-browser';
import { rowScalar } from '../encounter-map/ft3d/perception';
import './geometry-door-truth';
import { doorLockState } from '../janus-engine/geometry/door-lock-state.js';
import { planPartyRelocation } from '../janus-engine/movement/party-relocation.js';
import { planInventoryPlacement } from '../janus-engine/movement/placement-plan-22.js';
import type { InventorySpawnPlacement } from '../janus-engine/spawn/inventory-placement';
import { heldEncounterForAction } from '../shared/entity-compile-ready';
import { saveAppliedTruth } from '../shared/applied-truth-transport';
import { inventoryAppliedFrame, pushInventoryTruth, retireTransferredInventory, heldInventory } from '../shared/inventory-applied-truth';
import type { AppliedFrame } from '../janus-engine/transport/applied-frame';
import type { ActivityWrite } from '../janus-engine/actions/activity';
import type { ObjectManipulationBridge } from '../janus-engine/actions/object-manipulation-bridge';
import type { ObjectManipulationReceipt } from '../janus-engine/actions/object-manipulation-gate';
import { handleFt3dMovePreview } from './ft3d-move';
// Client-truth floor movement and carried drops share gravity, wind, damage and receipt delivery.
//
// Owner (2026-09-10 / 2026-09-16): "front end gate > back end gate". The pure resolver
// (janus-engine/inventory/floor-move.ts) decides from the roster + catalog this page already holds — legality
// (immovable / digsite / weight) and where the object comes to REST (the owner's gravity rule) — the roster row is
// moved in place and the FT3D scene re-projected at once; ONE commit then goes to
// inventory_floor_move_truth_commit_inc.php (target-row permission, row-state CAS, publish-first). Peers apply the
// `provisional` / `committed` / `correction` frames, and the server's typed encounter delta converges them by id.
// A handled drag never reaches the retired floor writer. Missing plans and unsupported stateful splits/merges
// are refused by the caller before a legacy request.
import {
  FLOOR_MOVE_SCHEMA, inventoryAttachment, inventoryFootprint, inventoryHeightFt, inventoryRestZ, inventorySolid, normalizeTransform, physicalMeshBoundsFt, resolveFloorMove, round3,
  type FootprintInputs, type RestMover, type RestRow, type RestShape, type RestWorld,
} from '../janus-engine/inventory/floor-move.js';
import { resolveTransformEdit, type TransformEdit } from '../janus-engine/inventory/transform-edit.js';
import { BLUDGEONING, FALL_ABILITY, FALL_UNMODELLED_OWN_LAYERS, fallTier } from '../janus-engine/inventory/object-fall-damage.js';
import { objectFallJanusUnmodelled, objectFallThroughJanus } from '../janus-engine/inventory/object-fall-janus.js';
import { type JanusReaders } from '../janus-engine/ability/context.js';
import { browserRollSource } from '../janus-engine/actions/roll-source.js';
import { applyObjectDestruction, ownEncounterLinks, type ObjectDestroyCommand, type ObjectDestroyFrame } from '../janus-engine/inventory/destroy-object.js';
import { classifyRefusal, RevertLedger, type OptimisticDraw, type TruthRefusal } from '../shared/revert-on-refusal.js';
import type { UndoReport } from '../shared/undo-frame.js';
import { campaignHouseRules } from '../janus-engine/ability/house-rules.js';
import { statusRows } from '../janus-engine/statuses/expiry.js';
import { planDamageDestruction, prefetchDamageDestruction } from './inventory-destroy-truth.js';
import { fetchAndCacheObject } from './object-cache.js';
import { dropPlan, liftWeight, WIND_WALL_ID, type WindPlan, type WindWallGeometry, type WindWorld } from '../janus-engine/inventory/wind-wall.js';
import { parsePaintedWall } from '../shared/painted-wall-geometry.js';
import { occupancyOverlap } from '../../../ft3d/engine/ft3d-move-core.js';
import { boundCurtainIds, boundDoorIds, COMPOUND_LINK_STATUSES, SIZE_WIDTHS_FT, creatureFacts, liftDestinationClear, liftObstacles, LiftUncertain, statusItemId,
  wallBindingIndex, phpInt, phpIsNumeric, type LiftInventoryFacts, type LiftMover, type LiftWorld } from '../janus-engine/inventory/lift-occupancy.js';
import { encounterDifficultFootprints } from '../janus-engine/movement/difficult-terrain.js';
import { actorEncounterFor, bankLegacyRowTruth, movementScopeReaders } from './movement-truth.js';
import { selectInventoryMeshSet } from '../../../ft3d/engine/mesh-presentation.js';
import { validateShape } from '../../../ft3d/engine/ft3d-shape-core.js';
import { refreshSavedInventory } from '../shared/inventory-saved-refresh';
import type { BasePatchFrame } from '../janus-engine/compile/base-patch';
import { normalizeInventoryTransform } from '../janus-engine/compile/inventory-transform';
import { planServerDrop, type ServerDropContext } from '../janus-engine/inventory/server-drop';
import { installServerDropBridge, serverDropCommand, type ProjectTransfer } from './server-drop-truth';
import type { InventoryMoveReceipt } from '../ft3d-move-handlers/inventory-move-receipt';
import { skipUnported } from '../janus-engine/unported';

type Row = Record<string, unknown>;
const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
const ENDPOINT = '/options/game/actions/inventory_floor_move_truth_commit_inc.php';
// A v1 receiver must reject a team move instead of silently saving only its object during deployment.
export const COMPOUND_FLOOR_MOVE_SCHEMA = 'janus.inventory.floor_move.v2';
const INTEGRATED_OBJECT_STATUS = '36C061BF-E480-4334-961C-26500150E3C4';
const LOCKED_STATUS = '477A7977-60D2-4B9C-96B0-6ED4BFD6437E';
const BINDINGS_ENDPOINT = '/options/game/actions/inventory_lock_bindings_read_inc.php';
const BINDINGS_TTL_MS = 10000;
const RETRIES = 3;

/** Operations this browser owns (their provisional/committed echoes change nothing here). */
const own = new Set<string>();
/** The newest local operation per row: an older operation's late failure never reverts a newer move (F4). */
const latestOp = new Map<string, string>();
const seen = new Map<string, Set<string>>();
function noteSeen(operationId: string, kind: string): boolean {
  const kinds = seen.get(operationId) ?? new Set<string>();
  if (kinds.has(kind) || (kind === 'provisional' && (kinds.has('committed') || kinds.has('correction')))) return false;
  kinds.add(kind); seen.set(operationId, kinds);
  if (seen.size > 256) seen.delete(seen.keys().next().value!);
  return true;
}

const inv = (row: Row): Row => asRow(row.inventory) ?? row;
const inventoryId = (row: Row | null): string => upper(row ? inv(row).inventory_id : '');
function position(row: Row): { x: number; y: number; z: number } {
  const pos = asRow(row.position) ?? {};
  const n = (v: unknown): number => { const x = Number(v); return Number.isFinite(x) ? x : 0; };
  return { x: n(pos.x_pos), y: n(pos.y_pos), z: n(pos.z_pos) };
}
function catalogOf(encounter: Row, row?: Row | null): Row {
  return row ? inventoryCatalog(row, encounter.catalog) : asRow(encounter.catalog) ?? {};
}
function definitionFor(encounter: Row, row: Row): Row | null {
  return inventoryDefinition(row, encounter.catalog);
}
const layer = (encounter: Row, row: Row, key: string): unknown => {
  try { return ftLayerValue({ ...row, catalog: catalogOf(encounter, row) }, key); } catch { return null; }
};
const itemsCol = (encounter: Row, row: Row, key: string): unknown => asRow(row.items)?.[key] ?? asRow(definitionFor(encounter, row)?.items)?.[key];
const uuidOf = (value: unknown): string | null => {
  const raw = Array.isArray(value) ? value[0] : value;
  const id = upper(typeof raw === 'string' ? raw : asRow(asRow(raw)?.items)?.item_id ?? asRow(raw)?.item_id);
  return UUID.test(id) ? id : null;
};
export function sizeBase(id: string): number | null {
  const list = (window as unknown as { SIZE_TYPES?: unknown[] }).SIZE_TYPES;
  if (!Array.isArray(list)) return SIZE_WIDTHS_FT[upper(id)] ?? null;
  const entry = list.map(asRow).find(t => upper(t?.item_id) === upper(id));
  const n = Number(entry?.base_size);
  return Number.isFinite(n) && n > 0 ? n : SIZE_WIDTHS_FT[upper(id)] ?? null;
}
/** The same selected world look used by cover and area targeting. */
function worldMeshSpace(encounter: Row, row: Row): ReturnType<typeof selectedPropMeshSpaceOrTier> {
  if (dedicatedPropSpace(String(itemsCol(encounter, row, 'item_type') ?? ''))) return null;
  return selectedPropMeshSpaceOrTier(row, definitionFor(encounter, row), catalogOf(encounter, row));
}
export function footprintInputs(encounter: Row, row: Row): FootprintInputs {
  const p = position(row);
  const def = definitionFor(encounter, row);
  const inventorySize = uuidOf(inv(row).inventory_size);
  const templateSizeId = def ? uuidOf((() => { try { return ftLayerValue(def, 'size'); } catch { return null; } })()) : null;
  const meshSpace = worldMeshSpace(encounter, row);
  return {
    itemType: String(itemsCol(encounter, row, 'item_type') ?? ''), x: p.x, y: p.y, z: p.z,
    transform: normalizeTransform(inv(row).transform_3d),
    sizeId: inventoryAuthoredSizeId(row, def),
    inventorySize,
    templateSizeId,
    worldMeshBoundsM: meshSpace?.boundsM ?? null,
    worldMeshAuthoredSizeTypeId: meshSpace?.authoredSizeTypeId,
    shape: encounterShapeValue(encounter),
  };
}
function generation(row: Row): Row | null {
  let raw = inv(row).inventory_generation;
  if (typeof raw === 'string') { try { raw = JSON.parse(raw); } catch { raw = null; } }
  return asRow(raw);
}
export function restRow(encounter: Row, row: Row): RestRow {
  const definition = definitionFor(encounter, row);
  if (!definition) throw new Error(`prop-template-not-held:${upper(ftInventoryField(row, 'inventory_item_id'))}`);
  const input = footprintInputs(encounter, row);
  const i = inv(row);
  const instanceValue = (key: string): unknown => layer(encounter, row, key) ?? (definition ? ftLayerValue(definition, key) : null);
  return {
    id: inventoryId(row), itemType: input.itemType, z: input.z,
    solid: inventorySolid({ sceneryShell: inventorySceneryShell(row),
      // PHP !empty(): a SECRET row (2) is private too, never a support (Codex review 2026-09-27 F8).
      private: Number(i.inventory_private ?? 0) !== 0, itemType: input.itemType, name: inventoryPropName(row, definition),
      ...inventoryFixtureFlags(instanceValue),
      meshDrawn: inventoryMeshDrawn(row, definition, catalogOf(encounter, row)),
      equipmentSlot: inventoryEquipmentSlot(row, definition), quantity: Number(ftInventoryField(row, 'item_qty') ?? 1),
      encounterLink: inventoryEncounterLink(row), wallRun: !!asRow(generation(row)?.wallRun), corpseLinked: uuidOf(i.inventory_encounter_creature_id) !== null,
    }),
    zone: inventoryFootprint(input, sizeBase),
    height: inventoryHeightFt(input, sizeBase),
  };
}
const statusIds = (row: Row): string[] => statusRows(row, 'target').map(statusItemId);
const storedFloorShapes = new WeakMap<Row, { revision: string; shape: RestShape | null }>();
const frozenFloorShapes = new WeakMap<Row, RestShape | null>();
const immutableGeometry = new WeakSet<object>();
function immutableGeometryTree(value: object): boolean {
  if (immutableGeometry.has(value)) return true;
  if (!Object.isFrozen(value) || !Object.values(value).every(child => !child || typeof child !== 'object' || immutableGeometryTree(child))) return false;
  immutableGeometry.add(value);
  return true;
}
/** Stored bytes are their own revision. The scene's opening projection freezes its entire tree and replaces it
 * on door/wall changes. Mutable, unversioned inputs must still be checked on every read, including after failure. */
function adoptFloorShape(encounter: Row, raw: unknown): RestShape | null {
  const revision = typeof raw === 'string' ? raw : null;
  const stored = revision === null ? undefined : storedFloorShapes.get(encounter);
  if (stored && stored.revision === revision) return stored.shape;
  const frozen = asRow(raw);
  const immutable = frozen !== null && immutableGeometryTree(frozen);
  if (immutable && frozenFloorShapes.has(frozen)) return frozenFloorShapes.get(frozen)!;
  let shape: RestShape | null = null;
  try {
    const input = revision === null ? raw : JSON.parse(revision);
    shape = input ? validateShape(input) as unknown as RestShape : null;
  } catch { /* A malformed authored revision stays unknown. */ }
  if (revision !== null) storedFloorShapes.set(encounter, { revision, shape });
  if (immutable) frozenFloorShapes.set(frozen, shape);
  return shape;
}
/** Borrow authored/live scene geometry; detach only rooms opened by an absent roof. Without a scene overlay,
 * overlayUnknown preserves the caller's refusal for decisions that need live door/wall/curtain state. */
export function encounterShape(encounter: Row, roster: Row[], provided?: unknown): { shape: RestShape | null; depthAuthored: boolean; overlayUnknown: boolean } {
  let raw = encounterShapeValue(encounter);
  const depthAuthored = raw !== null && raw !== undefined && raw !== '';
  // The FT3D scene hands over the shape it renders: the served (overlaid) shape with its live door states applied —
  // With it, a mid-air wall contact has current opening and wall state.
  let fromScene = false;
  if (depthAuthored && asRow(provided)) { raw = provided; fromScene = true; }
  let shape = adoptFloorShape(encounter, raw);
  if (!shape) return { shape: null, depthAuthored, overlayUnknown: false };
  const sourceShape = shape;
  const byId = new Map(roster.map(r => [inventoryId(r), r]));
  const roofs = asRow(asRow(shape.authoring)?.buildingRoofs) ?? {};
  for (const binding of Object.values(roofs).map(asRow)) {
    if (!binding?.inventoryId) continue;
    const row = byId.get(upper(binding.inventoryId));
    const current = row ? rowVitality(encounter, row, 'v_current') : null;
    const absent = !row || Number(readInventoryField(row, 'inventory_deleted') ?? 0) === 1
      || Number(readInventoryField(row, 'item_qty') ?? 1) <= 0 || (current !== null && current <= 0);
    if (!absent) continue;
    const roomIds = new Set((Array.isArray(binding.roomIds) ? binding.roomIds : []).map(String));
    for (let index = 0; index < shape.boxes.length; index++) {
      const box = shape.boxes[index]!;
      if (!roomIds.has(String(box.id ?? '')) || box.creation?.roofRemoved === true) continue;
      if (shape === sourceShape) shape = { ...shape, boxes: shape.boxes.slice() };
      shape.boxes[index] = { ...box, creation: { ...(box.creation ?? {}), roofRemoved: true } };
    }
  }
  const overlayUnknown = !fromScene && ((Array.isArray(shape.openings) && shape.openings.length > 0)
    || (Array.isArray(shape.sceneWalls) && shape.sceneWalls.length > 0)
    || roster.some(r => !!asRow(generation(r)?.wallRun)));
  return { shape, depthAuthored, overlayUnknown };
}
function rerender(encounterId: string, encounter: Row, subType: string, inventoryRows?: Row[], inventoryUnchanged = false): void {
  if (inventoryRows) {
    const ids = new Set(inventoryRows.map(row => inventoryId(row)));
    const terrainIds = Array.isArray(encounter.difficult_terrain_inventory_ids) ? encounter.difficult_terrain_inventory_ids : [];
    if (terrainIds.some(id => ids.has(upper(id)))) {
      const envelope = asRow(encounter._entity_base);
      const metadata = asRow(envelope?.metadata) as unknown as EncounterTerrainMetadata | null;
      if (!metadata) throw new Error('unported:terrain-delta-metadata');
      const patched = patchEncounterTerrainRows(encounter, encounter, ids, metadata);
      for (const key of ['difficult_terrain_rects', 'difficult_terrain_boxes', 'difficult_terrain_footprints', 'difficult_terrain_inventory_ids'])
        encounter[key] = patched[key];
      const footprints = Array.isArray(encounter.difficult_terrain_footprints) ? encounter.difficult_terrain_footprints as Row[] : [];
      inventoryRows = inventoryRows.map(row => ({ ...row, terrainFootprint: footprints.find(footprint => upper(footprint.inventory_id) === inventoryId(row)) ?? null }));
    }
  }
  window.dispatchEvent(new CustomEvent('ft:encounter-state-received', { detail: { encounterId, encounterArr: encounter, subType,
    ...(inventoryRows ? { inventoryRows } : {}), ...(inventoryUnchanged ? { inventoryUnchanged } : {}) } }));
}
/** A saved spot must also survive a compile prepared before this correction. */
function bankSavedFloorSpot(encounterId: string, id: string, p: Pos, cols: Row = {}): void {
  window.ftCorrectSavedRows?.(encounterId, [{ table: 'inventory', row_id: id, partial: true,
    row: { ...cols, x_pos: p.x, y_pos: p.y, z_pos: p.z } }]);
}
function setPosition(encounterId: string, id: string, p: { x: number; y: number; z: number }, subType: string): boolean {
  const encounter = actorEncounterFor(encounterId) as Row | null;
  const roster = Array.isArray(encounter?.encounter_inventories_arr) ? encounter!.encounter_inventories_arr as Row[] : null;
  const row = roster?.find(r => inventoryId(r) === id);
  if (!encounter || !row) return false;
  const now = position(row);
  if (Math.abs(now.x - p.x) < 1e-6 && Math.abs(now.y - p.y) < 1e-6 && Math.abs(now.z - p.z) < 1e-6) return false;
  // build_pos_arr(): only non-zero axes are carried; an absent axis IS 0.
  const next: Row = {};
  if (p.x !== 0) next.x_pos = p.x;
  if (p.y !== 0) next.y_pos = p.y;
  if (p.z !== 0) next.z_pos = p.z;
  if (Object.keys(next).length === 0) delete row.position; else row.position = next;
  rerender(encounterId, encounter, subType, [{ inventory: { inventory_id: id, transform_3d: readInventoryField(row, 'transform_3d') ?? null }, position: row.position ?? null }]);
  return true;
}
/** A row that left this floor (picked up, deleted) while a move was in flight: drop the local copy instead of
 * resurrecting it at an old spot (F7). The server's typed delta converges the rest. */
function removeRow(encounterId: string, id: string, subType: string): void {
  const encounter = actorEncounterFor(encounterId) as Row | null;
  const roster = Array.isArray(encounter?.encounter_inventories_arr) ? encounter!.encounter_inventories_arr as Row[] : null;
  const index = roster ? roster.findIndex(r => inventoryId(r) === id) : -1;
  if (!encounter || !roster || index < 0) return;
  roster.splice(index, 1);
  rerender(encounterId, encounter, subType, [{ inventory: { inventory_id: id, inventory_deleted: 1 } }]);
}
/** Put a saved transform (normalized object, or null = none) on the CURRENT roster row by id, and redraw — the stale /
 * correction path adopts the server's pin instead of rolling back to a snapshot (Codex review 2026-09-27, gestures F3). */
function setTransformById(encounterId: string, id: string, value: unknown, subType: string): void {
  const encounter = actorEncounterFor(encounterId) as Row | null;
  const roster = Array.isArray(encounter?.encounter_inventories_arr) ? encounter!.encounter_inventories_arr as Row[] : null;
  const row = roster?.find(r => inventoryId(r) === id);
  if (!encounter || !row) return;
  const i = inv(row);
  const next = asRow(value) ?? null;
  if (JSON.stringify(normalizeTransform(i.transform_3d)) === JSON.stringify(normalizeTransform(next))) return;
  if (next === null) delete i.transform_3d; else i.transform_3d = next;
  rerender(encounterId, encounter, subType, [row]);
}
/** Set / clear transform_3d.mounted on a roster row, keeping its stored representation (JSON string or object). */
function setMounted(row: Row, mounted: boolean): void {
  const i = inv(row);
  const raw = i.transform_3d;
  let t: Row = {};
  if (typeof raw === 'string') { try { t = asRow(JSON.parse(raw)) ?? {}; } catch { t = {}; } } else t = { ...(asRow(raw) ?? {}) };
  if (mounted) { t.schema = t.schema ?? 'ft.inventory-transform-3d.v1'; t.mounted = true; } else delete t.mounted;
  const empty = Object.keys(t).filter(k => k !== 'schema').length === 0;
  i.transform_3d = empty ? null : typeof raw === 'string' ? JSON.stringify(t) : t;
}
type Pos = { x: number; y: number; z: number };
type Binding = { lock_id: string; secured_id: string };
type FloorLinks = { rows: Binding[]; carried: Set<string> | null };
const bindingsCache = new Map<string, { at: number } & FloorLinks>();
/**
 * The floor's SQL-only links (cached 10 s): inventory_lock_bindings (the durable lock ↔ host edges the roster does not carry) and
 * ft_corpse_carried_creature_ids() (dead creatures whose body left this floor — `carried` is null from an endpoint without it).
 */
async function floorLinks(encounterId: string, fresh = false): Promise<FloorLinks> {
  const hit = bindingsCache.get(encounterId);
  if (!fresh && hit && performance.now() - hit.at < BINDINGS_TTL_MS) return hit;
  try {
    const response = await fetch(BINDINGS_ENDPOINT + '?encounter_id=' + encodeURIComponent(encounterId), { signal: AbortSignal.timeout(8000) });
    const body = asRow(await response.json());
    if (!response.ok || !Array.isArray(body?.bindings)) throw new Error('bindings unreadable');
    const rows = (body!.bindings as unknown[]).map(asRow).filter((r): r is Row => !!r)
      .map(r => ({ lock_id: upper(r.lock_id), secured_id: upper(r.secured_id) }));
    const carried = Array.isArray(body!.carried_creature_ids) ? new Set((body!.carried_creature_ids as unknown[]).map(upper).filter(Boolean)) : null;
    bindingsCache.set(encounterId, { at: performance.now(), rows, carried });
    return { rows, carried };
  } catch {
    if (hit && !fresh) return hit;
    throw new Error('lock bindings unreadable');
  }
}
const lockBindings = async (encounterId: string, fresh = false): Promise<Binding[]> => (await floorLinks(encounterId, fresh)).rows;
/**
 * ft_corpse_carried_creature_ids() for a Wind Wall plan, read FRESH (never the cached set: a body picked up or put down inside the cache
 * window would answer wrongly). Refreshes the shared cache; null (unknown → a dead row near the column hands back) on any failure.
 */
async function freshCarriedIds(encounterId: string, relocationFacts?: unknown): Promise<Set<string> | null> {
  // A join's authorized relocation read supplies the floor facts before membership is saved.
  const supplied = asRow(relocationFacts)?.[encounterId];
  if (Array.isArray(supplied)) return new Set(supplied.map(upper).filter(Boolean));
  try { return (await floorLinks(encounterId, true)).carried; } catch { return null; }
}
/**
 * shift_map_drag_lock_tandem_inventory()'s follower set, from what the page holds: the structural lock bindings (both
 * directions) and the integrated_object / locked statuses (a status on one row naming the other as caster, either way).
 * Only rows on this same floor follow. (The legacy shift wrote Δx and ΔELEVATION into x/z on scene axes — BUG LEDGER
 * 09-27; the truth lane moves followers by the full ground + elevation delta.)
 */
function followerIds(roster: Row[], id: string, bindings: Binding[]): string[] {
  const partners = new Set<string>();
  for (const b of bindings) { if (b.lock_id === id) partners.add(b.secured_id); if (b.secured_id === id) partners.add(b.lock_id); }
  for (const r of roster) {
    const rid = inventoryId(r);
    const statuses = asRow(r.statuses);
    const list = Array.isArray(statuses?.target) ? statuses!.target as unknown[] : [];
    for (const st of list.map(asRow)) {
      if (!st) continue;
      const sid = upper(asRow(st.items)?.item_id ?? st.status_item_id ?? asRow(st.status_arr)?.item_id);
      if (sid !== INTEGRATED_OBJECT_STATUS && sid !== LOCKED_STATUS) continue;
      const caster = upper(st.status_caster_id);
      if (!caster || caster === rid) continue;
      if (rid === id) partners.add(caster);
      if (caster === id) partners.add(rid);
    }
  }
  partners.delete(id);
  const onFloor = new Set(roster.map(inventoryId));
  return [...partners].filter(q => onFloor.has(q)).sort();
}
function creatureRow(encounter: Row, ecId: string): Row | null {
  return (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as Row[] : [])
    .find(c => upper(asRow(c.encounter_creatures)?.encounter_creature_id) === ecId) ?? null;
}
/** ft_corpse_after_body_write(): a BODY moved on its own floor carries its dead creature to the same spot. */
function setCreaturePosition(encounterId: string, ecId: string, p: Pos, subType: string): void {
  const encounter = actorEncounterFor(encounterId) as Row | null;
  const row = encounter ? creatureRow(encounter, ecId) : null;
  if (!encounter || !row) return;
  const next: Row = {};
  if (p.x !== 0) next.x_pos = p.x;
  if (p.y !== 0) next.y_pos = p.y;
  if (p.z !== 0) next.z_pos = p.z;
  if (Object.keys(next).length === 0) delete row.position; else row.position = next;
  rerender(encounterId, encounter, subType, undefined, true);
}
function warn(message: string): void {
  if (typeof displayWarning === 'function') displayWarning(message);
}

/** The rows that travel with a moved object — its lock-bound / integrated partners (same delta) and a body's dead creature —
 * or null when the lock bindings cannot be read (the caller hands back: unknown is never "none"). */
export async function linkedRows(encounter: Row, roster: Row[], row: Row, id: string, before: Pos, after: Pos, fresh = false): Promise<{
  followers: { id: string; before: Pos; after: Pos }[]; corpse: { encounter_creature_id: string; before: Pos; after: Pos } | null } | null> {
  const delta = { x: after.x - before.x, y: after.y - before.y, z: after.z - before.z };
  let bindings: Binding[];
  try { bindings = await lockBindings(upper(asRow(encounter.encounters)?.encounter_id) || upper(inv(row).possess_id), fresh); } catch { return null; }
  const followers = followerIds(roster, id, bindings).map(fid => {
    const fb = position(roster.find(r => inventoryId(r) === fid)!);
    return { id: fid, before: fb, after: { x: round3(fb.x + delta.x), y: round3(fb.y + delta.y), z: round3(fb.z + delta.z) } };
  });
  const corpseEc = uuidOf(rowScalar(row, 'inventory_encounter_creature_id'));
  const corpseRow = corpseEc ? creatureRow(encounter, corpseEc) : null;
  const encounterId = upper(ftEncounterField(encounter, 'encounter_id'));
  const follows = corpseRow && Number(rowScalar(corpseRow, 'encounter_creature_dead') ?? 0) === 1
    && Number(rowScalar(corpseRow, 'encounter_creature_deleted') ?? 0) === 0
    && Number(rowScalar(row, 'inventory_deleted') ?? 0) === 0
    && upper(rowScalar(row, 'possess_id')) === encounterId && upper(rowScalar(corpseRow, 'encounter_id')) === encounterId;
  return { followers, corpse: corpseEc && corpseRow && follows
    ? { encounter_creature_id: corpseEc, before: position(corpseRow), after: { ...after } } : null };
}

/** The page's campaign row for this floor (the house rules the fall's dice read). */
function campaignRowFor(encounter: Row): Row | null {
  const id = upper(asRow(encounter.encounters)?.encounter_master_parent_id ?? encounter.encounter_master_parent_id);
  const ft = (window as unknown as { FateTide?: { campaignCache?: Map<string, unknown>; currentCampaignArr?: unknown } }).FateTide;
  const candidates = [asRow(id ? ft?.campaignCache?.get(id) : null), asRow(ft?.currentCampaignArr)];
  return candidates.find(c => c && id && upper(asRow(c.campaigns)?.campaign_id ?? c.campaign_id) === id) ?? null;
}
type FallEnvelope = {
  dice: number; die: number; rolls_full: unknown[]; roll_result: number; feedback: Row;
  before: { v_max: number | null; v_current: number | null; v_temp: number | null }; v_current: number; v_temp: number;
  destroyed: null | { outcome: 'rubble' | 'vanish'; original_name: string; rubble: Row | null; frame: ObjectDestroyFrame };
  save: FloorFallSave;
};
/**
 * H3 → R2: the damage an object takes when it falls more than 5 ft (apply_fall_damage_to_inventory_in_feet twin), from the row +
 * catalog the page holds. LANE A's damageTypeRoll() decides it (objectFallThroughJanus — the object as caster AND target, as
 * PHP's janus() runs it); this reads its inputs and builds the Janus readers. Destroyed → the R1 resolver's rubble / vanish, the
 * pile at the rest point. Returns the envelope block plus the local apply / undo.
 */
function planFallDamage(encounter: Row, row: Row, id: string, fallFt: number, rest: Pos, resized?: ResizeFrame | null,
  savedBefore = savedFloorInventory(encounter, row)): { kind: 'none' } | { kind: 'handBack'; reason: string }
  | { kind: 'damage'; envelope: FallEnvelope; apply: () => void } {
  if (!(fallFt > 5)) return { kind: 'none' };
  const i = inv(row);
  const cat = catalogOf(encounter, row);
  const objectCache = (window as unknown as { FateTide?: { objectCache?: Map<string, unknown> } }).FateTide?.objectCache;
  // A FULL definition (an `items` bucket): the encounter catalog can hold a name-only stub for an id (a damage type's), which is
  // no definition — fall through to the object cache.
  const held = (key: string): Row | null => [asRow(cat[upper(key)]), asRow(objectCache?.get(upper(key)))].find(d => !!d && !!asRow(d.items)) ?? null;
  const entries = (target: Row, key: string): unknown[] => { try { const e = ftLayerEntries({ ...target, catalog: cat }, key); return Array.isArray(e) ? e : []; } catch { return []; } };
  const value = (target: Row, key: string): unknown => { try { return ftLayerValue({ ...target, catalog: cat }, key); } catch { return null; } };
  const hv = hydratedVitality(encounter, resized?.v_max === undefined ? row : { ...row, inventory: { ...i, v_max: resized.v_max, v_current: resized.v_current } });
  const vMax = hv.v_max ?? 0;
  const vCurrent = hv.v_current ?? vMax;
  const vTemp = hv.v_temp;
  // build_v_arr()'s non-living lean path: effective max = max(1, base max + base max temp).
  const effectiveVMax = hv.effective_v_max ?? Math.max(1, vMax);
  // resolve_negate_falling_damage_in_feet(): 'inf' wins, else the largest number.
  let negate: number | 'inf' = 0;
  for (const e of entries(row, 'negate_falling_damage_in_feet')) {
    const raw = asRow(e)?.item_layer_effect ?? e;
    if (String(raw).trim().toLowerCase() === 'inf') { negate = 'inf'; break; }
    const n = Number(raw); if (Number.isFinite(n) && n > (negate as number)) negate = n;
  }
  if (fallTier({ fallFt, negate, vMax }).verdict === 'none') return { kind: 'none' };
  const saved = savedBefore;
  if (!saved) return { kind: 'handBack', reason: 'destroy-source-unavailable' };
  const campaign = campaignRowFor(encounter);
  // R2: the route's pre-draw gate — only what the bypassed janus() wrapper acts on (by-id statuses, trigger carriers,
  // immune_to_ability, the privacy veto); everything the roll reads is Lane A's to answer or hand back, pre-draw. A status is
  // judged by its HELD definition; an unreadable status bucket hands back.
  const bucket = row.statuses;
  const targets = asRow(bucket)?.target;
  const statusList = Array.isArray(targets) ? targets : [];
  const malformedStatuses = (bucket !== undefined && bucket !== null && !asRow(bucket))
    || (targets !== undefined && targets !== null && !Array.isArray(targets)) || statusList.some(st => !asRow(st));
  const statuses = statusList.map(asRow).filter((st): st is Row => !!st).map(st => {
    const sid = upper(asRow(st.items)?.item_id ?? st.status_item_id ?? asRow(st.status_arr)?.item_id);
    const embedded = asRow(st.status_arr);
    const def = embedded && embedded.item_layer_types !== undefined ? embedded : held(sid);
    return { id: sid, definition: def && (def.item_layer_types !== undefined || asRow(def.items)) ? def : null };
  });
  const def = definitionFor(encounter, row);
  const ownLayerKeys = [...new Set([...Object.keys(asRow(def?.item_layer_types) ?? {}), ...Object.keys(asRow(row.item_layer_types) ?? {}),
    ...FALL_UNMODELLED_OWN_LAYERS.filter(layer => asRow(row)?.[layer] !== undefined && asRow(row)?.[layer] !== null)])]
    .filter(layer => entries(row, layer).length > 0 || (value(row, layer) ?? null) !== null || (asRow(row)?.[layer] ?? null) !== null);
  const w = window as unknown as { FateTide?: { user?: { isGM?: boolean } }; ftActiveViewerIsFullGm?: () => boolean };
  const elevated = w.FateTide?.user?.isGM === true || w.ftActiveViewerIsFullGm?.() === true;
  const unmodelled = malformedStatuses ? 'status-on-object' : objectFallJanusUnmodelled({
    ownLayerKeys, statuses, privateToViewer: Number(i.inventory_private ?? 0) === 1 && !elevated,
  });
  if (unmodelled) return { kind: 'handBack', reason: unmodelled };
  const ability = held(FALL_ABILITY.id);
  if (!ability) return { kind: 'handBack', reason: 'fall-ability-not-held' };
  // The Janus readers over what the page holds (the ability lane's shape, websocket/ability-truth.ts readers()): layer reads with
  // the encounter catalog attached (a thinned row resolves its template), status definitions from the catalog / object cache.
  const read: JanusReaders = {
    value: (target, key) => target ? ftLayerValue(target, key) : null,
    has: (target, key) => !!target && (ftLayerValue(target, key) != null || (ftLayerEntries(target, key) ?? []).length > 0),
    entries: (target, key) => target ? ftLayerEntries(target, key) ?? [] : [],
    statuses: target => (ftCreatureStatuses(target, 'target') ?? []).flatMap(entry => {
      const s = asRow(entry); if (!s) return [];
      const inline = asRow(s.status_arr);
      return [{ ...s, status_arr: inline?.item_layer_types !== undefined ? inline : held(upper(s.status_item_id)) ?? inline ?? null }];
    }),
    definition: key => held(key),
    // An object is never in the initiative order, never dead mid-fall, and has no reaction lane.
    inCombat: () => false, deadState: () => null, reactionInventoryComplete: () => false, visibility: () => null,
    houseRules: () => campaignHouseRules(campaign),
    campaignClock: () => null,
    addCampaignSeconds: () => { throw new Error('the object fall reads no campaign clock'); },
    uuid: () => crypto.randomUUID().toUpperCase(),
  };
  const encounterId = upper(asRow(encounter.encounters)?.encounter_id) || upper(i.possess_id);
  const campaignId = upper(asRow(campaign?.campaigns)?.campaign_id ?? campaign?.campaign_id);
  // Preflight the destructive dry runs, including their child splash branches and raw saved rows, before rolling.
  const destructionAt = (breakingDamage: number): ReturnType<typeof planDamageDestruction> =>
    planDamageDestruction({ encounterId, inventoryId: id, campaignId, rest, breakingDamage });
  const saveDestruction = (command: ObjectDestroyCommand): FloorFallSave =>
    floorFallSavePlan(saved, command.frame, resized?.v_current, command, key => savedFloorInventory(encounter, row, key));
  const verdict = objectFallThroughJanus({
    encounter, inventoryId: id, object: { ...row, catalog: cat }, fallFt, negate, ability, read,
    vitality: { vMax, vCurrent, vTemp, effectiveVMax },
    beforeDraw: (canDestroy, breakingDamage) => {
      if (!canDestroy) return null;
      for (const total of breakingDamage) {
        const plan = destructionAt(total);
        if (plan.kind === 'handBack') return plan.reason;
        saveDestruction(plan.command);
      }
      return null;
    },
  }, browserRollSource());
  if (verdict.verdict === 'none') return { kind: 'none' };
  // A post-draw hand-back would let PHP re-roll: never expected (the dry runs meet every dice-steered branch) — surfaced loudly.
  if (verdict.verdict === 'handBack') {
    if (verdict.postDraw) console.error('[inventory-floor-move-truth] object fall handed back AFTER the draw', verdict.reason);
    return { kind: 'handBack', reason: verdict.reason };
  }
  let destroyed: FallEnvelope['destroyed'] = null;
  let save: FloorFallSave = { inventory: [], statuses: [] };
  if (verdict.destroyed) {
    const plan = destructionAt(verdict.result);
    if (plan.kind === 'handBack') {
      console.error('[inventory-floor-move-truth] object destroyed by a fall the dry runs did not plan');
      return { kind: 'handBack', reason: plan.reason };
    }
    const c = plan.command;
    destroyed = { outcome: c.outcome as 'rubble' | 'vanish', original_name: c.log.original_name, rubble: c.rubble as unknown as Row | null, frame: c.frame };
    save = saveDestruction(c);
  }
  const envelope: FallEnvelope = {
    dice: verdict.dice, die: verdict.die, rolls_full: verdict.rollsFull, roll_result: verdict.result, feedback: verdict.feedback,
    before: savedFloorVitality(saved), v_current: verdict.vCurrent, v_temp: verdict.vTemp, destroyed,
    save,
  };
  return {
    kind: 'damage', envelope,
    apply: () => {
      if (destroyed) { applyObjectDestruction(encounter, destroyed.frame); rerender(encounterId, encounter, 'inventory_fall_destroyed'); return; }
      setVitalityById(encounterId, id, verdict.vCurrent, verdict.vTemp, 'inventory_fall_damage');
    },
  };
}
/** The object's composition definitions (their object_damage_type_* layers roll up into its defenses), its statuses', the fall
 * ability's and its damage type's: fetch any the page does not hold into the object cache before a damaging fall is planned. */
async function prefetchCompositions(encounter: Row, row: Row): Promise<void> {
  const cat = catalogOf(encounter, row);
  let list: unknown[] = [];
  try { const e = ftLayerEntries({ ...row, catalog: cat }, 'item_composition'); list = Array.isArray(e) ? e : []; } catch { list = []; }
  const cache = (window as unknown as { FateTide?: { objectCache?: Map<string, unknown> } }).FateTide?.objectCache;
  // R2: the definitions of the object's statuses too (the fall gate judges each by its layers).
  const statusList = Array.isArray(asRow(row.statuses)?.target) ? asRow(row.statuses)!.target as unknown[] : [];
  const statusIdsToHold = statusList.map(asRow).filter((st): st is Row => !!st && !(asRow(st.status_arr)?.item_layer_types !== undefined))
    .map(st => upper(asRow(st.items)?.item_id ?? st.status_item_id ?? asRow(st.status_arr)?.item_id)).filter(id => UUID.test(id));
  // R2-2: Lane A's roll reads the 'Take Fall Damage' definition and its damage type's (the per-die labels).
  const missing = [...list.filter(c => !(asRow(c) && asRow(asRow(c)?.items) && asRow(c)?.item_layer_types)).map(uuidOf), ...statusIdsToHold,
    FALL_ABILITY.id, BLUDGEONING.id]
    .filter((id): id is string => !!id && !asRow(asRow(cat[id])?.items) && !asRow(asRow(cache?.get(id))?.items));
  await Promise.all([...new Set(missing)].map(id => fetchAndCacheObject(id).catch(() => null)));
  await prefetchDamageDestruction({ encounterId: upper(ftEncounterField(encounter, 'encounter_id')), inventoryId: inventoryId(row) });
}
/** v_current / v_temp on the roster row (and its compiled vitality), by id. */
function setVitalityById(encounterId: string, id: string, vCurrent: number, vTemp: number, subType: string): void {
  const encounter = actorEncounterFor(encounterId) as Row | null;
  const roster = Array.isArray(encounter?.encounter_inventories_arr) ? encounter!.encounter_inventories_arr as Row[] : null;
  const row = roster?.find(r => inventoryId(r) === id);
  if (!encounter || !row) return;
  const i = inv(row);
  i.v_current = vCurrent; i.v_temp = vTemp;
  const cv = asRow(asRow(row.computed)?.vitality);
  if (cv) { cv.v_current = vCurrent; if (vTemp > 0) cv.v_temp = vTemp; else delete cv.v_temp; }
  rerender(encounterId, encounter, subType);
}

const PILOTED_STATUS = 'DDC66C31-4093-84AF-18BB-093D924B8738';
function encounterStatuses(encounter: Row): Row[] {
  const list = Array.isArray(asRow(encounter.statuses)?.target) ? asRow(encounter.statuses)!.target as unknown[] : Array.isArray(encounter.status_effects) ? encounter.status_effects as unknown[] : [];
  return list.map(asRow).filter((s): s is Row => !!s);
}
/** ft_painted_wall_wind_drop_sources() from the page: the encounter's active, unpaused Wind Wall anchors (stamped AOE sources). */
function windSources(encounter: Row, encounterId: string): Array<{ anchorId: string; casterId: string; wall: WindWallGeometry }> {
  const out: Array<{ anchorId: string; casterId: string; wall: WindWallGeometry }> = [];
  for (const st of encounterStatuses(encounter)) {
    const f = (k: string): unknown => { const v = st[k] ?? asRow(st.position)?.[k]; return Array.isArray(v) ? v[0] : v; };
    const paused = ['year', 'month', 'day', 'hour', 'minute', 'second'].some(u => Number(f('status_paused_campaign_' + u) ?? 0) !== 0);
    // ft_painted_wall_wind_cast_source(): status_origin_id ?? status_item_id (nullish — an empty origin is NOT Wind), the
    // encounter as target, UUID status / caster / connection ids. The anchor keeps its stored spelling (the legacy
    // strcmp tie-break between equal-time walls reads it raw).
    const originRaw = f('status_origin_id') ?? f('status_item_id') ?? asRow(st.items)?.item_id;
    if (Number(f('status_aoe_source') ?? 0) !== 1 || Number(f('status_deleted') ?? 0) !== 0 || paused || upper(originRaw) !== WIND_WALL_ID) continue;
    if (upper(f('status_target_id')) !== encounterId) continue;
    const wall = parsePaintedWall(f('status_aoe_geometry')) as unknown as WindWallGeometry | null;
    const anchorId = String(f('status_id') ?? ''), casterId = String(f('status_caster_id') ?? '');
    if (!wall || ![anchorId, casterId, String(f('status_connected_id') ?? '')].every(v => UUID.test(v.toUpperCase()))) continue;
    out.push({ anchorId, casterId, wall });
  }
  return out;
}
/** Link statuses (riding / piloting / grappling …) on a row: the casters they name. */
const linkCasters = (row: Row): string[] => {
  const list = asRow(row.statuses)?.target;
  return (Array.isArray(list) ? list : []).map(asRow).filter((st): st is Row => !!st && COMPOUND_LINK_STATUSES.has(statusItemId(st)))
    .map(st => upper(st.status_caster_id)).filter(Boolean);
};
/** The encounter as ft_encounter_occupancy_obstacles() reads it: every creature and inventory row as lift-occupancy facts. */
/** status_instance_has_campaign_pause(): any non-empty, non-zero status_paused_campaign_* value — TOP-level keys only, as PHP reads them. */
const statusPaused = (st: Row): boolean => ['year', 'month', 'day', 'hour', 'minute', 'second'].some(u => {
  let v = st['status_paused_campaign_' + u];
  if (Array.isArray(v)) v = v[0];
  if (v === null || v === undefined || v === '') return false;
  return !(phpIsNumeric(v) && phpInt(v) === 0);
});
/** collect_persistent_aoes()'s rows (unpaused): the encounter's, every inventory row's, and each creature's self-targeted auras. */
function persistentAoeStatuses(encounter: Row, roster: Row[]): Row[] {
  const targets = (row: Row): Row[] => { const list = asRow(row.statuses)?.target; return (Array.isArray(list) ? list : []).map(asRow).filter((s): s is Row => !!s); };
  const out = encounterStatuses(encounter).filter(st => !statusPaused(st));
  for (const r of roster) out.push(...targets(r).filter(st => !statusPaused(st)));
  for (const c of (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as unknown[] : []).map(asRow)) {
    if (!c) continue;
    const self = String(asRow(c.encounter_creatures)?.encounter_creature_id ?? '');
    out.push(...targets(c).filter(st => !statusPaused(st) && self !== '' && String(st.status_target_id ?? '') === self));
  }
  return out;
}
function liftWorld(encounter: Row, roster: Row[], world: RestWorld, carriedIds: ReadonlySet<string> | null): LiftWorld {
  const readers = { sizeBase, layerValue: (r: Row, name: string): unknown => { try { return ftLayerValue({ ...r, catalog: catalogOf(encounter, r) }, name); } catch { return null; } } };
  // A body on this floor names its dead creature: that creature is lying here, not carried (encounter_row_is_carried_body).
  const floorBodies = new Set(roster.filter(r => Number(inv(r).inventory_deleted ?? 0) === 0).map(r => uuidOf(inv(r).inventory_encounter_creature_id))
    .filter((v): v is string => !!v));
  const creatures = (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as unknown[] : []).map(asRow)
    .filter((c): c is Row => !!c).map(c => creatureFacts(c, readers, floorBodies, carriedIds));
  const restById = new Map(world.rows.map(r => [r.id, r]));
  const rows: LiftInventoryFacts[] = roster.map(r => {
    const input = footprintInputs(encounter, r), id = inventoryId(r), rest = restById.get(id) ?? restRow(encounter, r);
    const terrain = layer(encounter, r, 'terrain_type');
    return { id, itemId: upper(inv(r).inventory_item_id), itemType: input.itemType, solid: rest.solid, zone: rest.zone, z: rest.z,
      stepOverSizeId: inventoryStepOverSizeId(input.sizeId, physicalMeshBoundsFt(input, sizeBase)),
      tierFt: (input.sizeId ? sizeBase(input.sizeId) : null) ?? 5, scale: input.transform?.scale ?? 1,
      terrainTyped: terrain !== null && terrain !== undefined && terrain !== '', links: linkCasters(r) };
  });
  // The held terrain index is authoritative, including an explicitly empty list.
  const stamped = encounter.difficult_terrain_inventory_ids;
  const difficultIds = new Set((Array.isArray(stamped) ? stamped.map(upper)
    : encounterDifficultFootprints(encounter).map(asRow).map(f => upper(f?.inventory_id))).filter(Boolean));
  // Painted-wall sections bind inventory rows that collide as their wall section at the apex (ft_painted_wall_inventory_geometry_index).
  const walls = wallBindingIndex(persistentAoeStatuses(encounter, roster));
  const rawShape = asRow(encounter.encounters)?.encounter_shape ?? encounter.encounter_shape;
  // MV5: bound doors are not standing obstacles (ft_inventory_is_bound_door).
  return { creatures, rows, difficultIds, walls, boundCurtainIds: boundCurtainIds(rawShape), boundDoorIds: boundDoorIds(rawShape) };
}
/** MV6 (2026-10-03, R-008 `creature-space`): the encounter's lift world as the WALK ROUTER reads it (walk-route-obstacles.ts →
 *  ft_route_encounter_move()'s $hard_obstacles), over the live inventory rows; a body the page cannot prove carried stays `carried: null`
 *  unless the caller passes the floor's ft_corpse_carried_creature_ids() (`carriedIds`, see freshCarriedIds). */
export function routeLiftWorld(encounter: Row, carriedIds: ReadonlySet<string> | null = null): { world: LiftWorld; rows: Row[]; rest: RestWorld; overlayUnknown: boolean } {
  const roster = (Array.isArray(encounter.encounter_inventories_arr) ? encounter.encounter_inventories_arr as Row[] : [])
    .filter(r => Number(inv(r).inventory_deleted ?? 0) === 0);
  const es = encounterShape(encounter, roster);
  const rest: RestWorld = { shape: es.shape, depthAuthored: es.depthAuthored, rows: roster.map(r => restRow(encounter, r)) };
  return { world: liftWorld(encounter, roster, rest, carriedIds), rows: roster, rest, overlayUnknown: es.overlayUnknown };
}
/**
 * H4: does an active Wind Wall intercept this drop (movement, then gravity)? ft_painted_wall_wind_resolve_drop()'s decision,
 * from the page. The lift column's two server reads — the occupancy obstacles and the destination resolver at the apex — are
 * twinned in lift-occupancy.ts: a row whose facts the page cannot establish hands back only when it could reach the column
 * ('lift-…'), and a shape without its live opening overlays hands back only when the apex sits against a face.
 */
function planWind(encounter: Row, roster: Row[], row: Row, id: string, world: RestWorld, mover: (x: number, y: number) => RestMover,
  input: FootprintInputs, origin: Pos, requested: Pos, rest: { z: number; rest: string }, overlayUnknown: boolean, carriedIds: ReadonlySet<string> | null): { kind: 'none' } | { kind: 'handBack'; reason: string }
  | { kind: 'wind'; anchorId: string; casterId: string; plan: WindPlan } {
  const encounterId = upper(asRow(encounter.encounters)?.encounter_id) || upper(inv(row).possess_id);
  const sources = windSources(encounter, encounterId);
  if (!sources.length) return { kind: 'none' };
  const i = inv(row);
  // computed weight gross ?? computed_value(weight, total) — a lean row carries none; PHP hydrates the template's.
  const computedWeight = asRow(asRow(row.computed)?.weight);
  const templateWeight = asRow(asRow(definitionFor(encounter, row)?.computed)?.weight);
  const gross = numOrNull(computedWeight?.gross) ?? numOrNull(computedWeight?.total) ?? numOrNull(templateWeight?.total);
  const weight = liftWeight({ possessId: upper(i.possess_id) || encounterId, encounterId, deleted: Number(i.inventory_deleted ?? 0) !== 0,
    private: Number(i.inventory_private ?? 0) !== 0, qty: Number(i.item_qty ?? 1), equipped: !!Number(i.equipped ?? 0), immovable: !!layer(encounter, row, 'immovable'),
    mounted: normalizeTransform(i.transform_3d)?.mounted === true, itemType: input.itemType, sizeId: input.sizeId,
    grossLb: gross, unitWeightLb: numOrNull(layer(encounter, row, 'weight')) });
  if (weight === null || statusIds(row).includes(PILOTED_STATUS)) return { kind: 'none' };
  const height = mover(origin.x, origin.y).height;
  const footprintAt = (x: number, y: number, z: number) => { const zone = inventoryFootprint(input, sizeBase, { x, y }); return { ...zone, zMin: z, zMax: z + height }; };
  const lift = liftWorld(encounter, roster, world, carriedIds);
  const team = layer(encounter, row, 'team');
  const liftMover: LiftMover = { id, tierFt: (input.sizeId ? sizeBase(input.sizeId) : null) ?? 5,
    team: team === null || team === undefined || team === '' || typeof team === 'object' ? null : String(team) };
  const inside = Number(asRow(encounter.encounters)?.encounter_inside ?? encounter.encounter_inside ?? 0);
  const windWorld: WindWorld = {
    shape: world.shape as unknown as WindWorld['shape'],
    footprintAt,
    attachment: (x, y, z, contact) => { const m = mover(x, y); return inventoryAttachment(world, m, x, y, z, contact); },
    obstacles: volume => liftObstacles(lift, liftMover, volume),
    overlap: occupancyOverlap as unknown as WindWorld['overlap'],
    // Unreachable without a shape: ft_painted_wall_wind_inventory_plan() never lifts on a shapeless encounter (census 2026-09-27).
    liftDestinationClear: (x, y, z, fromZ) => { if (!world.shape) throw new LiftUncertain('lift-unshaped'); return liftDestinationClear({ shape: world.shape, canvasExterior: inside !== 1,
      persisted: { x, y, z: fromZ }, footprintAt, heightFt: height, tierFt: liftMover.tierFt, overlayUnknown,
      obstacles: m => liftObstacles(lift, liftMover, m, true) }, x, y, z); },
    restZ: (x, y, z, contact) => inventoryRestZ(world, mover(x, y), x, y, z, contact),
  };
  try {
    const candidate = dropPlan(sources, windWorld, () => windWorld, id, weight, origin, requested, true, rest);
    if (!candidate) return { kind: 'none' };
    const source = sources.find(src => src.anchorId === candidate.source.anchorId)!;
    return { kind: 'wind', anchorId: source.anchorId, casterId: source.casterId, plan: candidate.plan };
  } catch (error) {
    return { kind: 'handBack', reason: error instanceof LiftUncertain ? error.message : 'wind-' + String(error) };
  }
}

/** One landing proof and one wire shape for GM object moves, Push/Pull and transform settlement. */
export function planFloorCreatureFollowers(encounter: Row, row: Row, after: Pos, excluded = new Set<string>(),
  route?: ReadonlyArray<readonly number[]>): { members: CreatureFollower[]; reason: null } | { reason: string; members?: never } {
  const id = inventoryId(row);
  if (!latchedCreatures(encounter, id).length) return { members: [], reason: null };
  const base = movementScopeReaders(encounter), world = routeLiftWorld(encounter);
  const plan = planLatchedObjectMove(encounter, world.rest.shape ?? null, row, after, {
    ...base, baseSizeFt: member => base.geometry(member)?.diameter ?? null,
    heightFt: member => base.geometry(member)?.height ?? null,
    propFootprint: member => {
      const input = footprintInputs(encounter, inventoryId(member) === id ? row : member), zone = inventoryFootprint(input, sizeBase);
      return { ...zone, heightFt: inventoryHeightFt(input, sizeBase) };
    },
    crossingWorld: () => ({ shape: world.rest.shape ?? null, canvasExterior: !world.rest.depthAuthored, overlayUnknown: world.overlayUnknown }),
  }, excluded, route);
  if (plan.kind !== 'move') return { reason: plan.kind === 'handback' ? plan.reason : 'latched-team-not-held' };
  const members = plan.members.map(member => ({ id: member.id,
    expected_rev: Number(ftEncounterCreatureField(creatureRow(encounter, member.id)?.encounter_creatures, 'movement_rev') ?? 0),
    before: member.before, after: member.after }));
  return validCreatureFollowers(members) ? { members, reason: null } : { reason: 'Invalid creature followers' };
}

export type FloorMoveOutcome = 'handled' | 'refused' | null;
export interface FloorMoveRequest {
  encounterId: string; inventoryId: string;
  handDrop?: { actorId: string; quantity: number };
  quantity?: number;
  /** The scene's drop: ground pair (x, y) + feet elevation (z). */
  x: number; y: number; z: number;
  feetMoved?: number; clientInstanceId?: string; sequence?: number;
  /** Edit Scene height gesture: `height` (Z arrows — pins when nothing holds it) / `drop` (ground control — unpins). */
  gesture?: 'height' | 'drop' | null;
  /** The scene's rendered shape (served overlays + live door states); without it a mid-air wall answer hands back. */
  shape?: unknown;
  /** The Place-object / Edit Scene writer moves the whole row (a stack travels together). */
  wholeRow?: boolean;
  /** Undo / redo: the position this editor last saved (the CAS `before`, like the transform endpoint's expected_position_ft). */
  expected?: { x: number; y: number; z: number } | null;
  /** The FT3D scene's Place-object / Edit Scene writer reconciles its placement from the committed answer (or the
   * saved spot on a refusal). Not called on a hand-back: the caller's legacy callback runs instead. */
  onSettled?: (result: { ok: true; positionFt: { x: number; y: number; z: number }; rest: string; mounted?: boolean }
    | { ok: false; message: string; positionFt: { x: number; y: number; z: number } | null }) => void;
}

type CreatureFollowerFrame = Pos & { id: string; rev?: number };
/** The settled path and movement price belong to the browser; PHP only relays this receipt. */
function floorMovementReceipt(before: Pos, after: Pos, feet?: number | null) {
  const points = [{ x: before.x, y: before.y, z: before.z }];
  if (before.x !== after.x || before.y !== after.y || before.z !== after.z) points.push({ x: after.x, y: after.y, z: after.z });
  return {
    accepted_path: { schema: 'ft.ft3d.accepted-move-path.v1' as const, points },
    speed_variable: 'speed_walking',
    movement_cost_ft: feet ?? Math.hypot(after.x - before.x, after.y - before.y),
  };
}
type Envelope = {
  schema: typeof FLOOR_MOVE_SCHEMA | typeof COMPOUND_FLOOR_MOVE_SCHEMA | typeof HAND_DROP_SPLIT_SCHEMA; operation_id: string; encounter_id: string; inventory_id: string; actor_id: string | null;
  before: { possess_id: string; x: number; y: number; z: number; qty: number; deleted: 0; transform: unknown };
  hand_drop?: HandDropSnapshot;
  after: { x: number; y: number; z: number; mounted?: boolean }; gesture: 'height' | 'drop' | null; rest: string; fall_ft: number;
  followers: { id: string; before: Pos; after: Pos }[];
  creature_followers?: CreatureFollower[];
  corpse: { encounter_creature_id: string; before: Pos; after: Pos } | null;
  ft_moved: number | null; client_instance_id: string; sequence: number;
  movement_receipt?: ReturnType<typeof floorMovementReceipt>;
  /** H3: the fall's damage, decided here (absent = no damaging fall). */
  fall_damage?: FallEnvelope;
  /** H4: the Wind Wall that caught the drop — its anchor, its caster and the plan (lift apex, settled point, fall). */
  wind?: { anchor_id: string; caster_id: string; plan: WindPlan; roll_result?: number };
  impact_skip?: { kind: 'janus_unported_skip'; reason: string };
  server_drop?: { kind: 'inventory-impact' };
};

type FloorDraw = Pick<OptimisticDraw, 'accept' | 'refuse'> & {
  correct?: (refusal: TruthRefusal, applySaved: () => void) => UndoReport | null;
};
/** Send all touched positions and presentation receipts in the one applied frame. */
export function pushFloorMoveTruth(envelope: { operation_id: string; encounter_id: string; actor_id: string | null } & Row): void {
  own.add(envelope.operation_id);
  if (own.size > 256) own.delete(own.values().next().value!);
  const after = asRow(envelope.after)!;
  const iid = upper(envelope.inventory_id), drop = asRow(envelope.hand_drop), split = asRow(drop?.split);
  const destination = split ? upper(split.inventory_id) : iid;
  const damage = asRow(envelope.fall_damage), manipulation = asRow(envelope.manipulation);
  const receipt = manipulation?.receipt as ObjectManipulationReceipt | undefined;
  let transform = after.transform;
  if (!('transform' in after) && typeof after.mounted === 'boolean') {
    const probe = { inventory: { transform_3d: readInventoryField(heldInventory(iid), 'transform_3d') } };
    setMounted(probe, after.mounted); transform = probe.inventory.transform_3d;
  }
  const hasTransform = 'transform' in after || typeof after.mounted === 'boolean';
  const writes: ActivityWrite[] = [{ table: 'inventory', id: destination, before: split ? null : {}, after: {
    ...(split ? { inventory_item_id: readInventoryField(heldInventory(iid), 'inventory_item_id'), item_qty: split.item_qty } : {}),
    x_pos: after.x, y_pos: after.y, z_pos: after.z,
    ...(drop ? { possess_id: envelope.encounter_id, equipped: '', inventory_wares: 0 } : {}),
    ...(hasTransform ? { transform_3d: transform } : {}),
    ...Object.fromEntries(['inventory_size', 'v_max', 'v_current'].filter(key => key in after).map(key => [key, after[key]])),
    ...(damage && !damage.destroyed ? { v_current: damage.v_current, v_temp: damage.v_temp } : {}),
  } }];
  if (receipt) writes.push({ table: 'encounter_creatures', id: receipt.actor.id, before: {}, after: {
    x_pos: receipt.actor.after.x, y_pos: receipt.actor.after.y, z_pos: receipt.actor.after.z,
    aps: receipt.actor.aps, movement_pct: receipt.actor.movement_pct, movement_rev: receipt.actor.movement_rev,
  } });
  if (split) writes.push({ table: 'inventory', id: iid, before: {}, after: { item_qty: split.source_qty_after } });
  const followers = Array.isArray(envelope.followers) ? envelope.followers as Array<{ id: string; after: Pos }> : [];
  const team = Array.isArray(envelope.creature_followers) ? envelope.creature_followers as CreatureFollower[] : [];
  for (const f of followers) writes.push({ table: 'inventory', id: f.id, before: {}, after: { x_pos: f.after.x, y_pos: f.after.y, z_pos: f.after.z } });
  const corpse = asRow(envelope.corpse);
  for (const f of [...team, ...(corpse ? [{ id: upper(corpse.encounter_creature_id), after: corpse.after as Pos }] : [])])
    writes.push({ table: 'encounter_creatures', id: f.id, before: {}, after: { x_pos: f.after.x, y_pos: f.after.y, z_pos: f.after.z,
      ...('expected_rev' in f ? { movement_rev: Number(f.expected_rev) + 1, movement_last_operation_id: envelope.operation_id } : {}) } });
  const wire = { ...envelope, schema: FLOOR_MOVE_SCHEMA, inventory_id: destination, position: after,
    followers: followers.map(f => ({ id: f.id, ...f.after })), creature_followers: team.map(f => ({ id: f.id, ...f.after, rev: f.expected_rev + 1 })),
    corpse: corpse ? { encounter_creature_id: corpse.encounter_creature_id, ...asRow(corpse.after) } : null,
    ...(hasTransform ? { transform,
      resize: Object.fromEntries(['inventory_size', 'v_max', 'v_current'].filter(key => key in after).map(key => [key, after[key]])) } : {}),
    ...(damage ? { fall_damage: { ...damage, frame: asRow(damage.destroyed)?.frame } } : {}),
    ...(manipulation ? { manipulation_receipt: manipulation.receipt } : {}),
  };
  pushInventoryTruth('janus_inventory_floor_move', wire, inventoryAppliedFrame(wire, writes));
}
async function commit(envelope: Envelope, legacy: () => void, startedAt: number, onSettled: FloorMoveRequest['onSettled'] | undefined,
  draw: FloorDraw): Promise<void> {
  envelope.movement_receipt = floorMovementReceipt(envelope.before, envelope.after, envelope.ft_moved);
  if (envelope.wind) envelope.wind.roll_result = Math.round(envelope.wind.plan.apexZ - envelope.wind.plan.from.z);
  pushFloorMoveTruth(envelope);
  const settle = (result: Parameters<NonNullable<FloorMoveRequest['onSettled']>>[0]): void => { try { onSettled?.(result); } catch { /* the scene's own problem */ } };
  const encounterId = envelope.encounter_id, id = envelope.hand_drop?.split?.inventory_id ?? envelope.inventory_id;
  let savedCreatureFollowers: CreatureFollowerFrame[] = [];
  let savedCorpse: InventoryFloorMoveWireFrame['corpse'];
  const newest = (): boolean => latestOp.get(id) === envelope.operation_id && ownsFloorPosition(encounterId, 'inventory', id, envelope.operation_id);
  // restore: a saved position (adopt it) · null with `gone` (the row left the floor: drop it) · undefined (put it back).
  // Refused position writes recover only their own still-current facts; falls retain their scoped destructive write set.
  const refused = (message: string | null, restore: { x: number; y: number; z: number } | null | undefined, gone = false,
    refusal: TruthRefusal = classifyRefusal({ status: 500, body: { error: message ?? 'error' } })!, savedTransform?: { value: unknown }): void => {
    // an OLDER op's refusal leaves the row to the newer local move that superseded it (Codex review: value equality alone cannot tell
    // which op owns a slot); the newer op's own answer settles it
    if (!newest()) draw.accept();
    else {
      const applySaved = (): void => {
        if (gone) removeRow(encounterId, id, 'inventory_floor_move_gone');
        else if (restore) {
          bankSavedFloorSpot(encounterId, id, restore, savedTransform ? { transform_3d: savedTransform.value } : {});
          if (savedTransform) setTransformById(encounterId, id, savedTransform.value, 'inventory_floor_move_saved_transform');
          setPosition(encounterId, id, restore, 'inventory_floor_move_reverted');
        }
        if (savedCorpse && finitePos(savedCorpse) && ownsFloorPosition(encounterId, 'creature', savedCorpse.encounter_creature_id, envelope.operation_id))
          setCreaturePosition(encounterId, upper(savedCorpse.encounter_creature_id), savedCorpse, 'inventory_floor_move_saved_corpse');
      };
      if (draw.correct) draw.correct(refusal, applySaved);
      else { draw.refuse(refusal); applySaved(); }
    }
    for (const member of savedCreatureFollowers) if (ownsFloorPosition(encounterId, 'creature', member.id, envelope.operation_id))
      applyCreatureFollower(encounterId, member, 'inventory_floor_move_refused_team');
    settle({ ok: false, message: message ?? 'The move was not saved.', positionFt: gone ? null : restore ?? { x: envelope.before.x, y: envelope.before.y, z: envelope.before.z } });
    if (message) warn(message);
    window.dispatchEvent(new CustomEvent('ft:inventory-floor-move-truth-refused', { detail: { envelope, message } }));
  };
  for (let attempt = 1; attempt <= RETRIES; attempt++) {
    let response: Response;
    try {
      response = await saveAppliedTruth(ENDPOINT, envelope, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(envelope), signal: AbortSignal.timeout(20000) });
    } catch (thrown) {
      if (attempt < RETRIES) { await new Promise(r => setTimeout(r, 400 * attempt)); continue; }
      refused('The move could not be saved (network). The object was put back.', undefined, false, classifyRefusal({ thrown })!);
      return;
    }
    let result: Row | null = null;
    try { result = await response.json() as Row; } catch { result = null; }
    if (response.ok && result?.ok === true) {
      for (const follower of Array.isArray(result.creature_followers) ? result.creature_followers as CreatureFollowerFrame[] : [])
        applyCreatureFollower(encounterId, follower, 'inventory_floor_move_saved_team');
      draw.accept();
      applySavedFall(encounterId, asRow(result.fall_damage)?.saved);
      if (result.base_refresh) await window.ftApplyBasePatch?.(result.base_refresh as BasePatchFrame).catch(error => console.error('[inventory-floor-move] saved base refresh', error));
      settle({ ok: true, positionFt: { x: envelope.after.x, y: envelope.after.y, z: envelope.after.z }, rest: envelope.rest,
        ...(typeof envelope.after.mounted === 'boolean' ? { mounted: envelope.after.mounted } : {}) });
      window.dispatchEvent(new CustomEvent('ft:inventory-floor-move-truth-committed', { detail: { envelope, result, ms: Math.round((performance.now() - startedAt) * 10) / 10 } }));
      if (envelope.impact_skip && result.replay !== true) skipUnported(envelope.impact_skip.reason);
      return;
    }
    savedCreatureFollowers = Array.isArray(result?.creature_followers) ? result.creature_followers as CreatureFollowerFrame[] : [];
    savedCorpse = asRow(result?.corpse) as InventoryFloorMoveWireFrame['corpse'];
    const error = String(result?.error ?? response.statusText ?? 'error');
    if (response.status === 422 && error === 'outside-boundary') {
      // Outside I1: put the row back and hand the drop to the legacy endpoint unchanged.
      window.dispatchEvent(new CustomEvent('ft:inventory-floor-move-truth-handback', { detail: { envelope, boundary: result?.boundary ?? null } }));
      // A newer local move of this row already owns it: neither revert nor replay this older drop.
      if (!newest()) return;
      draw.refuse(classifyRefusal({ status: response.status, body: result })!);
      legacy();
      return;
    }
    if (response.status === 409 && error === 'stale') {
      const saved = asRow(result?.saved);
      const restore = saved ? { x: Number(saved.x), y: Number(saved.y), z: Number(saved.z) } : null;
      // Adopt the saved pin (another editor may have changed it) rather than rolling back to this page's snapshot; followers and the
      // body revert with the frame (H2 review F3).
      const savedTransform = saved && Object.prototype.hasOwnProperty.call(saved, 'transform') ? { value: saved.transform } : undefined;
      refused(restore ? 'That object changed before your move landed. It was put back where it is saved.' : 'That object is no longer on the floor.', restore, restore === null,
        classifyRefusal({ status: response.status, body: result })!, savedTransform);
      return;
    }
    const refusal = classifyRefusal({ status: response.status, body: result })!;
    if (response.status >= 400 && response.status < 500) { refused(`The move was rejected (${error}). The object was put back.`, undefined, false, refusal); return; }
    if (attempt < RETRIES) { await new Promise(r => setTimeout(r, 400 * attempt)); continue; }
    refused(`The move could not be saved (${error}). The object was put back.`, undefined, false, refusal);
    return;
  }
}

/**
 * Null = this page lacks a supported plan; the caller refuses without posting.
 * 'refused' restores the saved spot. 'handled' = planned, drawn, committing.
 */
export async function tryCommitFloorInventoryMove(request: FloorMoveRequest, legacy: () => void): Promise<FloorMoveOutcome> {
  const startedAt = performance.now();
  const encounterId = upper(request.encounterId), sourceId = upper(request.inventoryId);
  let id = sourceId;
  if (!UUID.test(encounterId) || !UUID.test(id)) return null;
  // H4 / S4: the carried-body set is a SQL read. Await it BEFORE this function takes its one synchronous snapshot of the roster, so the
  // plan never mixes pre- and post-await rows (only when the floor has a Wind Wall — every other drag stays await-free here).
  const preview = actorEncounterFor(encounterId) as Row | null;
  if (preview) await hydrateInventoryDefinitions(readInventoryRows(preview, 'encounter_inventories_arr'), preview.catalog, fetchAndCacheObject);
  const carried = !request.gesture && preview && windSources(preview, upper(asRow(preview.encounters)?.encounter_id) || encounterId).length
    ? await freshCarriedIds(upper(asRow(preview.encounters)?.encounter_id) || encounterId) : null;
  const encounter = actorEncounterFor(encounterId) as Row | null;
  const roster = Array.isArray(encounter?.encounter_inventories_arr) ? encounter!.encounter_inventories_arr as Row[] : null;
  let row = roster?.find(r => inventoryId(r) === id) ?? null;
  let handDrop: HandDropSnapshot | undefined;
  if(request.handDrop) {
    // The item can belong to a chest, corpse or another creature. Its stored holder is the CAS source.
    row = asRow(await getProximityEntity(id));
    if(!row || inventoryId(row)!==id)return null;
    row.catalog={...asRow(encounter?.catalog),...asRow(row.catalog)};
    try{handDrop=handDropSnapshot(row,request.handDrop.quantity);}catch(error){refuseUnported(error instanceof Error?error.message:String(error));return 'refused';}
    if(statusIds(row).includes(INTEGRATED_OBJECT_STATUS)||uuidOf(rowScalar(row,'inventory_encounter_creature_id'))){refuseUnported('hand-drop:carried-link-settlement-unported');return 'refused';}
  }
  if (!encounter || !roster || !row) return null;
  const sourceRow = row;
  const sourceBefore = savedFloorInventory(encounter, sourceRow);
  let savedBefore = sourceBefore;
  if (handDrop?.split) {
    id = handDrop.split.inventory_id;
    const fields = { inventory_id: id, item_qty: handDrop.split.item_qty, possess_id: encounterId, equipped: null, inventory_wares: 0 };
    row = looseFloorRow(sourceRow, encounterId);
    Object.assign(inv(row), fields);
    savedBefore = sourceBefore ? { ...sourceBefore, inventory: { ...inv(sourceBefore), ...fields } } : null;
  }
  if(!request.handDrop && request.quantity!==undefined && Number(rowScalar(row,'item_qty')??1)!==request.quantity){refuseUnported('floor-move:partial-stack-unported');return 'refused';}
  const i = inv(row);
  const possess = upper(rowScalar(sourceRow,'possess_id')) || encounterId;
  let verdict;
  let before: { x: number; y: number; z: number };
  let actorId: string | null;
  try {
    const w = window as unknown as { FateTide?: { user?: { isGM?: boolean } }; ftActiveViewerIsFullGm?: () => boolean; ftGetActiveCasterId?: () => unknown };
    const elevated = w.FateTide?.user?.isGM === true || w.ftActiveViewerIsFullGm?.() === true;
    const editor = elevated && typeof ftIsModifyEncounterToolsPage === 'function' && ftIsModifyEncounterToolsPage();
    actorId = editor ? null : uuidOf(request.handDrop?.actorId ?? w.ftGetActiveCasterId?.());
    const actor = actorId ? (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as Row[] : [])
      .find(c => upper(asRow(c.encounter_creatures)?.encounter_creature_id) === actorId) ?? null : null;
    if (actorId && !actor) { if (elevated) actorId = null; else return null; }
    const computed = asRow(asRow(row.computed)?.weight);
    const capacity = actor ? (asRow(asRow(asRow(asRow(actor.computed)?.creature)?.capacity)?.push_drag_lift)?.total
      ?? (() => { try { return ftLayerValue(actor, 'creature_push_drag_lift_capacity'); } catch { return null; } })()) : null;
    const layerWeight = Number(layer(encounter, row, 'weight'));
    const gross = Number(computed?.gross ?? computed?.total);
    const movingGross = handDrop?.split ? gross * handDrop.split.item_qty / Number(rowScalar(sourceRow, 'item_qty') ?? 1) : gross;
    const expected = request.expected;
    before = expected && [expected.x, expected.y, expected.z].every(n => Number.isFinite(Number(n)))
      ? { x: round3(Number(expected.x)), y: round3(Number(expected.y)), z: round3(Number(expected.z)) } : position(row);
    const { shape, depthAuthored, overlayUnknown } = encounterShape(encounter, roster, request.shape);
    // A missing authored shape cannot be treated as an empty ground plane.
    if (depthAuthored && !shape) return null;
    const world: RestWorld = { shape, depthAuthored, rows: roster.map(r => restRow(encounter, r)) };
    const input = footprintInputs(encounter, row);
    const transform = normalizeTransform(i.transform_3d);
    const mover: RestMover = {
      id, mounted: transform?.mounted === true, integrated: statusIds(row).includes(INTEGRATED_OBJECT_STATUS),
      footprintAt: (x, y) => inventoryFootprint(input, sizeBase, { x, y }), height: inventoryHeightFt(input, sizeBase),
    };
    const vMax = rowVitality(encounter, row, 'v_max');
    verdict = resolveFloorMove({
      encounterId, inventoryId: id,
      // A carried row retains its last floor coordinates for CAS, not its release height.
      row: { possessId: request.handDrop ? encounterId : possess, deleted: Number(i.inventory_deleted ?? 0) === 1, qty: Number(i.item_qty ?? 1), ...before,
        ...(request.handDrop ? { z: round3(request.z) } : {}), transformSha: '' },
      to: { x: round3(request.x), y: round3(request.y), z: round3(request.z) },
      elevated: elevated || !!request.handDrop, explicitEditorSurface: editor, actorId, immovable: !!layer(encounter, row, 'immovable'), itemType: input.itemType,
      weight: Number.isFinite(movingGross) || Number.isFinite(layerWeight)
        ? { grossStackLb: Number.isFinite(movingGross) ? movingGross : null, layerWeightLb: Number.isFinite(layerWeight) ? layerWeight : null, itemQty: Number(i.item_qty ?? 1), capacityRaw: capacity }
        : null,
      shapeOverlayUnknown: overlayUnknown,
      hasVitality: vMax === null ? null : vMax > 0,
      gesture: request.handDrop ? 'drop' : request.gesture === 'height' || request.gesture === 'drop' ? request.gesture : null,
      wholeRow: request.wholeRow === true,
    }, world, mover);
  } catch (error) {
    window.dispatchEvent(new CustomEvent('ft:inventory-floor-move-slice-unavailable', { detail: { reason: String(error), inventoryId: id } }));
    return null;
  }
  if (verdict.verdict === 'handBack') {
    window.dispatchEvent(new CustomEvent('ft:inventory-floor-move-slice-unavailable', { detail: { reason: verdict.reason, inventoryId: id } }));
    return null;
  }
  if (verdict.verdict === 'refuse') {
    warn(verdict.message);
    try { request.onSettled?.({ ok: false, message: verdict.message, positionFt: { ...before! } }); } catch { /* scene */ }
    // The scene drew the object at the drop point: re-project the roster (unchanged) so it returns to its saved spot.
    rerender(encounterId, encounter, 'inventory_floor_move_refused');
    return 'refused';
  }
  // H4: an active Wind Wall intercepts a plain drag (the legacy height gestures never consulted it).
  let windBlock: { anchor_id: string; caster_id: string; plan: WindPlan } | null = null;
  if (!request.gesture) {
    const input = footprintInputs(encounter, row);
    const transform = normalizeTransform(i.transform_3d);
    const es = encounterShape(encounter, roster, request.shape);
    const world: RestWorld = { shape: es.shape, depthAuthored: es.depthAuthored, rows: roster.map(r => restRow(encounter, r)) };
    const moverAt = (): RestMover => ({ id, mounted: !request.handDrop && transform?.mounted === true, integrated: statusIds(row).includes(INTEGRATED_OBJECT_STATUS),
      footprintAt: (x, y) => inventoryFootprint(input, sizeBase, { x, y }), height: inventoryHeightFt(input, sizeBase) });
    const windRow = request.handDrop ? looseFloorRow(row, encounterId) : row;
    if (request.handDrop) setMounted(windRow, false);
    const wind = planWind(encounter, roster, windRow, id, world, moverAt, input, before!, { x: round3(request.x), y: round3(request.y), z: round3(request.z) },
      { z: verdict.after.z, rest: verdict.rest }, es.overlayUnknown, carried);
    if (wind.kind === 'handBack') {
      window.dispatchEvent(new CustomEvent('ft:inventory-floor-move-slice-unavailable', { detail: { reason: wind.reason, inventoryId: id } }));
      return null;
    }
    if (wind.kind === 'wind') {
      windBlock = { anchor_id: wind.anchorId, caster_id: wind.casterId, plan: wind.plan };
      verdict = { ...verdict, after: { x: round3(wind.plan.to.x), y: round3(wind.plan.to.y), z: round3(wind.plan.to.z) }, rest: 'fell', fallFt: round3(wind.plan.fallFt) };
    }
  }
  // Linked objects follow by the same delta; a body carries its dead creature.
  const linked = request.handDrop ? {followers: [], corpse: null} : await linkedRows(encounter, roster, row, id, before!, verdict.after);
  if (!linked) {
    window.dispatchEvent(new CustomEvent('ft:inventory-floor-move-slice-unavailable', { detail: { reason: 'lock-bindings-unreadable', inventoryId: id } }));
    return null;
  }
  const { followers, corpse } = linked;
  if (verdict.fallFt > 5) await prefetchCompositions(encounter, row);
  const fall = planFallDamage(encounter, row, id, verdict.fallFt, verdict.after, undefined, savedBefore);
  if (fall.kind === 'handBack') {
    window.dispatchEvent(new CustomEvent('ft:inventory-floor-move-slice-unavailable', { detail: { reason: 'fall-' + fall.reason, inventoryId: id } }));
    return null;
  }
  const team = planFloorCreatureFollowers(encounter, row, verdict.after,
    new Set([...followers.map(member => member.id), ...(corpse ? [corpse.encounter_creature_id] : [])]));
  if (team.reason !== null) {
    const message = `The latched team cannot land there: ${team.reason}`;
    warn(message); rerender(encounterId, encounter, 'latch_move_refused');
    request.onSettled?.({ ok: false, message, positionFt: { ...before! } }); return 'refused';
  }
  const creatureFollowers = team.members;
  const operationId = crypto.randomUUID().toUpperCase();
  const envelope: Envelope = {
    schema: handDrop?.split ? HAND_DROP_SPLIT_SCHEMA : creatureFollowers.length ? COMPOUND_FLOOR_MOVE_SCHEMA : FLOOR_MOVE_SCHEMA,
    operation_id: operationId, encounter_id: encounterId, inventory_id: sourceId, actor_id: actorId,
    before: { possess_id: possess, ...before!, qty: Number(rowScalar(sourceRow,'item_qty') ?? 1), deleted: 0, transform: rowScalar(sourceRow,'transform_3d') ?? null },
    ...(handDrop ? {hand_drop:handDrop} : {}),
    after: { ...verdict.after, ...(typeof verdict.mounted === 'boolean' ? { mounted: verdict.mounted } : {}) },
    gesture: request.handDrop ? 'drop' : request.gesture === 'height' || request.gesture === 'drop' ? request.gesture : null,
    rest: verdict.rest, fall_ft: round3(verdict.fallFt), followers, corpse,
    ...(creatureFollowers.length ? { creature_followers: creatureFollowers } : {}),
    ft_moved: Number.isFinite(Number(request.feetMoved)) ? Number(request.feetMoved) : null,
    client_instance_id: String(request.clientInstanceId ?? ''), sequence: Number.isSafeInteger(request.sequence) ? request.sequence! : 0,
    ...(fall.kind === 'damage' ? { fall_damage: fall.envelope } : {}),
    ...(windBlock ? { wind: windBlock } : {}),
  };
  own.add(operationId);
  if (own.size > 256) own.delete(own.values().next().value!);
  latestOp.set(id, operationId);
  const mutate = () => {
    if(request.handDrop)return; // The holder changes on commit; do not mutate an uncaptured carried tree.
    // A height gesture may pin / unpin: mirror it on the roster row's transform (the saved row converges peers).
    if (typeof verdict.mounted === 'boolean') setMounted(row, verdict.mounted);
    for (const f of followers) setPosition(encounterId, f.id, f.after, 'inventory_floor_move_follower');
    if (corpse) setCreaturePosition(encounterId, corpse.encounter_creature_id, corpse.after, 'inventory_floor_move_corpse');
    for (const member of creatureFollowers) setCreaturePosition(encounterId, member.id, member.after, 'inventory_floor_move_team');
    setPosition(encounterId, id, verdict.after, 'inventory_floor_move_local');
    // Land before damage (the legacy order): a destroyed object leaves its pile at the rest point.
    if (fall.kind === 'damage') fall.apply();
  };
  const movedIds = [id, ...followers.map(member => member.id)];
  const companions = [...(corpse ? [corpse.encounter_creature_id] : []), ...creatureFollowers.map(member => member.id)];
  const draw = drawFloorEffects(encounterId, operationId, request.handDrop ? [] : movedIds, companions, {
    inventoryId: id, transform: !request.handDrop && typeof verdict.mounted === 'boolean',
    ...(fall.kind === 'damage' ? { damage: { ...fall.envelope, frame: fall.envelope.destroyed?.frame } } : {}),
  }, mutate);
  window.dispatchEvent(new CustomEvent('ft:inventory-floor-move-truth-applied', { detail: { envelope } }));
  void commit(envelope, legacy, startedAt, request.onSettled, draw);
  return 'handled';
}

/** Raw transfer already committed (and already paid AP). Only its actual destination's aftermath is drawn/saved here. */
async function settleServerInventoryDrop(receipt: InventoryMoveReceipt, context: ServerDropContext, project: ProjectTransfer): Promise<InventoryMoveReceipt> {
  const destination = receipt.move.destination, id = upper(destination.inventory_id), eid = upper(destination.possess_id);
  const rawPoint = { ...destination.position };
  let projected = false;
  const adopt = (row: Row | null): void => {
    destination.deleted_after = !row || Number(readInventoryField(row, 'inventory_deleted') ?? 0) !== 0;
    if (row) {
      destination.possess_id = String(readInventoryField(row, 'possess_id') ?? destination.possess_id);
      destination.item_qty_after = Number(readInventoryField(row, 'item_qty') ?? 1);
      destination.position = position(row);
      destination.row = row;
    } else destination.item_qty_after = 0;
    if (upper(receipt.move.source.inventory_id) === id) {
      receipt.move.source.item_qty_after = destination.item_qty_after;
      receipt.move.source.deleted_after = destination.deleted_after;
    }
    if (!projected) { projected = true; project(receipt); }
    const encounter = actorEncounterFor(eid) as Row | null;
    if (!encounter) return;
    const roster = readInventoryRows(encounter, 'encounter_inventories_arr');
    const index = roster.findIndex(member => inventoryId(member) === id);
    if (destination.deleted_after || upper(destination.possess_id) !== eid) { if (index >= 0) roster.splice(index, 1); }
    else if (row) { if (index >= 0) roster[index] = row; else roster.push(row); }
    encounter.encounter_inventories_arr = roster;
    rerender(eid, encounter, 'server_drop_saved');
  };
  try {
    const sourceId = upper(receipt.move.source.inventory_id);
    if (sourceId !== id && !receipt.move.source.deleted_after) {
      const source = await fetchAndCacheObject(sourceId, { fresh: true }) as Row | null;
      if (!source || inventoryId(source) !== sourceId) throw new Error('server-drop:source-identity-not-held');
      receipt.move.source.item_qty_after = Number(readInventoryField(source, 'item_qty') ?? 1);
      receipt.move.source.deleted_after = Number(readInventoryField(source, 'inventory_deleted') ?? 0) !== 0;
    }
    const row = await fetchAndCacheObject(id, { fresh: true }) as Row | null;
    if (!row || inventoryId(row) !== id) throw new Error('server-drop:destination-identity-not-held');
    adopt(row);
    // A replay after settlement, destruction or another action adopts saved truth without drawing.
    if (!row || destination.deleted_after || upper(destination.possess_id) !== eid
      || Number(readInventoryField(row, 'item_qty') ?? 1) <= 0
      || (!receipt.move.outcome.endsWith('merge') && ['x', 'y', 'z'].some(axis => Math.abs(position(row)[axis as keyof Pos] - rawPoint[axis as keyof Pos]) > .001))) return receipt;
    const encounter = actorEncounterFor(eid) as Row | null;
    if (!encounter) throw new Error('server-drop:encounter-not-held');
    await hydrateInventoryDefinitions(readInventoryRows(encounter, 'encounter_inventories_arr'), encounter.catalog, fetchAndCacheObject);
    const roster = readInventoryRows(encounter, 'encounter_inventories_arr');
    const saved = savedFloorInventory(encounter, row);
    if (!saved) throw new Error('server-drop:saved-destination-not-held');
    const es = encounterShape(encounter, roster, context.shape);
    if (es.depthAuthored && !es.shape) throw new Error('server-drop:shape-not-held');
    const world: RestWorld = { shape: es.shape, depthAuthored: es.depthAuthored, rows: roster.map(member => restRow(encounter, member)) };
    const input = footprintInputs(encounter, row), statuses = statusIds(row);
    const actorId = context.actorId && creatureRow(encounter, upper(context.actorId)) ? upper(context.actorId) : null;
    const mover: RestMover = { id, mounted: normalizeTransform(readInventoryField(row, 'transform_3d'))?.mounted === true,
      integrated: statuses.includes(INTEGRATED_OBJECT_STATUS), footprintAt: (x, y) => inventoryFootprint(input, sizeBase, { x, y }), height: inventoryHeightFt(input, sizeBase) };
    const carried = !receipt.move.outcome.endsWith('merge');
    let rest = planServerDrop(world, mover, position(row), statuses.includes(PILOTED_STATUS), carried);
    let windBlock: Envelope['wind'];
    let windReason: string | null = null;
    if (windSources(encounter, eid).length && !statuses.includes(PILOTED_STATUS)) {
      const probe = { ...row, inventory: { ...inv(row), transform_3d: { ...normalizeTransform(readInventoryField(row, 'transform_3d')), mounted: carried ? false : mover.mounted } } };
      const wind = planWind(encounter, roster, probe, id, world, () => ({ ...mover, mounted: carried ? false : mover.mounted }),
        footprintInputs(encounter, probe), position(row), position(row), { z: rest.after.z, rest: rest.rest }, es.overlayUnknown, await freshCarriedIds(eid));
      if (wind.kind === 'wind') {
        windBlock = { anchor_id: wind.anchorId, caster_id: wind.casterId, plan: wind.plan };
        rest = { ...rest, after: wind.plan.to, rest: 'fell', fallFt: round3(wind.plan.fallFt) };
      } else if (wind.kind === 'handBack') windReason = 'wind-' + wind.reason;
    }
    if (rest.fallFt > 5) await prefetchCompositions(encounter, row);
    // Serialization happens before the POST; a reload after a lost answer reuses these exact dice and ids.
    const envelope = serverDropCommand<Envelope>(upper(receipt.mutation_id), () => {
      const fall = planFallDamage(encounter, row, id, rest.fallFt, rest.after, undefined, saved);
      const name = inventoryPropName(row, definitionFor(encounter, row)) || 'Object';
      const reasons = [windReason, fall.kind === 'handBack' ? fall.reason : null].filter(Boolean);
      return { schema: FLOOR_MOVE_SCHEMA, operation_id: upper(receipt.mutation_id), encounter_id: eid, inventory_id: id, actor_id: actorId,
        server_drop: { kind: 'inventory-impact' },
        before: { possess_id: upper(readInventoryField(saved, 'possess_id')), ...position(row), qty: Number(readInventoryField(saved, 'item_qty') ?? 1), deleted: 0,
          transform: readInventoryField(saved, 'transform_3d') },
        after: { ...rest.after, mounted: rest.unpin ? false : mover.mounted }, gesture: 'drop', rest: rest.rest, fall_ft: rest.fallFt,
        followers: [], corpse: null, ft_moved: null, client_instance_id: '', sequence: 0,
        ...(fall.kind === 'damage' ? { fall_damage: fall.envelope } : {}),
        ...(windBlock ? { wind: windBlock } : {}),
        ...(reasons.length ? { impact_skip: { kind: 'janus_unported_skip' as const,
          reason: `${name}: server-inventory-impact:native-unported (${reasons.join('; ')})` } } : {}),
      };
    });
    if (rest.fallFt === 0 && !rest.unpin && !envelope.impact_skip) return receipt;
    own.add(envelope.operation_id);
    if (own.size > 256) own.delete(own.values().next().value!);
    latestOp.set(id, envelope.operation_id);
    const draw = drawFloorEffects(eid, envelope.operation_id, [id], [], { inventoryId: id, transform: true,
      ...(envelope.fall_damage ? { damage: { ...envelope.fall_damage, frame: envelope.fall_damage.destroyed?.frame } } : {}) }, () => {
      setMounted(row, envelope.after.mounted === true);
      setPosition(eid, id, envelope.after, 'server_drop_landed');
      if (envelope.fall_damage) applyFloorFrame({ schema: FLOOR_MOVE_SCHEMA, operation_id: envelope.operation_id, encounter_id: eid, inventory_id: id,
        position: envelope.after, fall_damage: envelope.fall_damage.destroyed ? { frame: envelope.fall_damage.destroyed.frame }
          : { v_current: envelope.fall_damage.v_current, v_temp: envelope.fall_damage.v_temp } }, 'provisional');
    });
    let committed = false;
    await commit(envelope, () => { throw new Error('server-drop:legacy-settlement-forbidden'); }, performance.now(), result => {
      committed = result.ok;
      if (result.positionFt) destination.position = { ...result.positionFt };
    }, draw);
    const finalRow = await fetchAndCacheObject(id, { fresh: true }) as Row | null;
    if (finalRow) adopt(finalRow);
    else if (committed && envelope.fall_damage?.destroyed) adopt(null);
    else await window.ftResyncEncounter?.(eid);
  } catch (error) {
    // The transfer remains committed even when its settlement cannot be delivered. Never replay it or refund/charge AP here.
    if (!projected) { projected = true; project(receipt); }
    await window.ftResyncEncounter?.(eid).catch(() => undefined);
    warn('The item was transferred; its landing could not be confirmed.');
    window.dispatchEvent(new CustomEvent('ft:server-drop-settlement-failed', { detail: { inventoryId: id, reason: String(error) } }));
  }
  return receipt;
}
installServerDropBridge(settleServerInventoryDrop);

export type InventoryFloorMoveWireFrame = {
  applied_frame?: AppliedFrame;
  movement_receipt?: ReturnType<typeof floorMovementReceipt>;
  before?: Pos;
  client_instance_id?: string;
  sequence?: number;
  manipulation_receipt?: ObjectManipulationReceipt;
  schema: string; operation_id: string; encounter_id: string; inventory_id: string; position: { x?: number; y?: number; z?: number };
  provisional?: boolean; committed?: boolean; correction?: boolean; restore?: { x: number; y: number; z: number } | null; reason?: string; rest?: string;
  restore_transform?: unknown;
  base_refresh?: BasePatchFrame | null;
  /** H3: the fall's saved vitality, or its destruction frame. */
  fall_damage?: { v_current?: number; v_temp?: number; frame?: ObjectDestroyFrame; saved?: Row[] } | null;
  /** Gesture 'transform': the saved next transform, and a resize's size band + vitality. */
  transform?: unknown;
  resize?: ResizeFrame | null;
  restore_resize?: ResizeFrame | null;
  restore_followers?: { id: string; x: number; y: number; z: number }[];
  restore_corpse?: { encounter_creature_id: string; x: number; y: number; z: number } | null;
  creature_followers?: CreatureFollowerFrame[];
  restore_creature_followers?: CreatureFollowerFrame[];
  followers?: { id: string; x: number; y: number; z: number }[];
  corpse?: { encounter_creature_id: string; x: number; y: number; z: number } | null;
};
/** Fall/transform recovery is limited to the command's write set, including new piles. Ordinary position moves never capture/diff rows. */
function destructionInventoryIds(frame?: ObjectDestroyFrame): string[] {
  return [...new Set([...(frame?.remove ?? []), ...(frame?.add ?? []).map(row => inventoryId(row as unknown as Row)), ...(frame?.released ?? []).map(inventoryId),
    ...(frame?.retarget ?? []).map(row => row.inventory_id), ...(frame?.vitality ?? []).map(row => row.inventory_id)].map(upper))];
}

/** The floor command's other writes. Removed rows stay exact references; only written slots retain before/after values. */
function drawFloorEffects(encounterId: string, operationId: string, inventoryIds: string[], creatureIds: string[], effects: {
  inventoryId: string; transform?: boolean; resize?: ResizeFrame | null; damage?: InventoryFloorMoveWireFrame['fall_damage'];
}, mutate: () => void): FloorDraw {
  const frame = effects.damage?.frame;
  if (!effects.transform && !effects.resize && !effects.damage) return drawFloorPositions(encounterId, operationId, inventoryIds, creatureIds, mutate);
  const encounter = actorEncounterFor(encounterId) as Row | null;
  if (!encounter) return drawFloorPositions(encounterId, operationId, inventoryIds, creatureIds, mutate);
  const inventories = readInventoryRows(encounter, 'encounter_inventories_arr');
  const find = (id: string): Row | undefined => inventories.find(row => inventoryId(row) === upper(id));
  const finishes: Array<() => void> = [], corrections: Array<(report: UndoReport) => void> = [];
  const slots = new Map<Row, Set<string>>();
  const capture = (row: Row, path: string[], id: string, emptyContainerOnly = false): void => {
    const key = path.join('.'), keys = slots.get(row) ?? new Set<string>();
    if (keys.has(key)) return;
    keys.add(key); slots.set(row, keys);
    let parent: Row | null = row;
    for (let index = 0; index < path.length - 1; index++) {
      if (!asRow(parent?.[path[index]!])) capture(row, path.slice(0, index + 1), id, true);
      parent = asRow(parent?.[path[index]!]);
    }
    // These are physical slot references (including absence), not new semantic field reads or copies.
    const read = (): { present: boolean; value: unknown } => {
      let holder: Row | null = row;
      for (const part of path.slice(0, -1)) holder = asRow(holder?.[part]);
      const last = path[path.length - 1]!;
      return { present: !!holder && Object.prototype.hasOwnProperty.call(holder, last), value: holder?.[last] };
    };
    const before = read();
    finishes.push(() => {
      const after = read();
      if (before.present === after.present && before.value === after.value) return;
      corrections.push(report => {
        const current = read();
        const held = actorEncounterFor(encounterId) as Row | null;
        if (!ownsFloorPosition(encounterId, 'inventory', id, operationId) || !held
          || !readInventoryRows(held, 'encounter_inventories_arr').includes(row)
          || current.present !== after.present || current.value !== after.value
          || emptyContainerOnly && Object.keys(asRow(current.value) ?? {}).length > 0) { report.conflicts++; report.conflictPaths.push(`${id}.${key}`); return; }
        let holder = row;
        for (const part of path.slice(0, -1)) holder = asRow(holder[part]) ?? (holder[part] = {}) as Row;
        if (before.present) holder[path[path.length - 1]!] = before.value; else delete holder[path[path.length - 1]!];
        report.reverted++;
      });
    });
  };
  const row = find(effects.inventoryId);
  if (row) {
    if (effects.transform) capture(row, ['inventory', 'transform_3d'], effects.inventoryId);
    if (effects.resize) for (const key of ['inventory_size', 'v_max', 'v_current'] as const)
      if (key === 'inventory_size' || effects.resize[key] !== undefined) capture(row, ['inventory', key], effects.inventoryId);
    if (effects.damage && !frame) for (const key of ['v_current', 'v_temp']) {
      capture(row, ['inventory', key], effects.inventoryId);
      capture(row, ['computed', 'vitality', key], effects.inventoryId);
    }
  }
  for (const vitality of frame?.vitality ?? []) {
    const target = find(vitality.inventory_id);
    if (target) for (const path of [['inventory', 'v_current'], ['computed', 'vitality', 'v_current'], ['computed', 'vitality', 'base_v_current']]) capture(target, path, vitality.inventory_id);
  }
  for (const retarget of frame?.retarget ?? []) for (const link of ownEncounterLinks(find(retarget.inventory_id) ?? null))
    if (upper(link.encounter_link_id) === upper(retarget.encounter_link_id)) {
      // Link slots are inside a retained floor row, so bind their identity check to that row.
      const before = link.encounter_link_to;
      finishes.push(() => {
        const after = link.encounter_link_to;
        if (before === after) return;
        corrections.push(report => {
          const held = actorEncounterFor(encounterId) as Row | null;
          const target = held && readInventoryRows(held, 'encounter_inventories_arr').find(member => inventoryId(member) === upper(retarget.inventory_id));
          if (!ownsFloorPosition(encounterId, 'inventory', retarget.inventory_id, operationId) || !target
            || !ownEncounterLinks(target).includes(link) || link.encounter_link_to !== after) { report.conflicts++; return; }
          link.encounter_link_to = before; report.reverted++;
        });
      });
    }
  const captureRows = (key: 'encounter_inventories_arr' | 'encounter_creatures_arr', ids: string[], kind: 'inventory' | 'creature'): void => {
    const identify = (member: Row): string => kind === 'inventory' ? inventoryId(member) : upper(ftEncounterCreatureField(member.encounter_creatures, 'encounter_creature_id'));
    const rows = Array.isArray(encounter[key]) ? encounter[key] as Row[] : [];
    for (const id of new Set(ids.map(upper))) {
      const index = rows.findIndex(member => identify(member) === id), before = rows[index], previous = rows[index - 1], next = rows[index + 1];
      finishes.push(() => {
        const after = rows.find(member => identify(member) === id);
        if (before === after) return;
        corrections.push(report => {
          const current = actorEncounterFor(encounterId) as Row | null;
          const live = Array.isArray(current?.[key]) ? current![key] as Row[] : [];
          const at = live.findIndex(member => identify(member) === id);
          if (!ownsFloorPosition(encounterId, kind, id, operationId) || live[at] !== after) { report.conflicts++; return; }
          if (at >= 0) live.splice(at, 1);
          if (before) {
            const nextIndex = next ? live.indexOf(next) : -1, previousIndex = previous ? live.indexOf(previous) : -1;
            live.splice(nextIndex >= 0 ? nextIndex : previousIndex >= 0 ? previousIndex + 1 : Math.min(index, live.length), 0, before);
          }
          report.reverted++;
        });
      });
    }
  };
  captureRows('encounter_inventories_arr', [...(frame?.remove ?? []), ...(frame?.add ?? []).map(member => inventoryId(member as unknown as Row)), ...(frame?.released ?? []).map(inventoryId)], 'inventory');
  captureRows('encounter_creatures_arr', frame?.creaturesRemoved ?? [], 'creature');
  const extraClaims = [...destructionInventoryIds(frame).map(id => ({ kind: 'inventory' as const, id })),
    ...(frame?.creaturesRemoved ?? []).map(id => ({ kind: 'creature' as const, id }))]
    .filter(({ kind, id }) => !(kind === 'inventory' ? inventoryIds : creatureIds).some(member => upper(member) === upper(id)))
    .map(({ kind, id }) => {
      const key = `${encounterId}:${kind}:${upper(id)}`, previous = latestFloorWrites.get(key);
      latestFloorWrites.set(key, upper(operationId)); return { key, previous };
    });
  const positions = drawFloorPositions(encounterId, operationId, inventoryIds, creatureIds, () => { mutate(); for (const finish of finishes) finish(); });
  let pending = true;
  const correct = (refusal: TruthRefusal, applySaved: () => void): UndoReport | null => {
    if (!pending) return null;
    pending = false;
    const report: UndoReport = { reverted: 0, kept: 0, conflicts: 0, conflictPaths: [] };
    // Restore removed rows before their position/field slots; retain the original roster/row identities.
    for (let index = corrections.length - 1; index >= 0; index--) corrections[index]!(report);
    const positionReport = positions.correct!(refusal, applySaved);
    if (positionReport) {
      report.reverted += positionReport.reverted; report.kept += positionReport.kept; report.conflicts += positionReport.conflicts;
      report.conflictPaths.push(...positionReport.conflictPaths);
    }
    for (const claim of extraClaims) if (latestFloorWrites.get(claim.key) === upper(operationId)) {
      if (claim.previous === undefined) latestFloorWrites.delete(claim.key); else latestFloorWrites.set(claim.key, claim.previous);
    }
    const current = actorEncounterFor(encounterId) as Row | null;
    if (current && report.reverted) {
      if (frame) rerender(encounterId, current, 'inventory_floor_move_reverted');
      else rerender(encounterId, current, 'inventory_floor_move_reverted', readInventoryRows(current, 'encounter_inventories_arr').filter(member => inventoryId(member) === upper(effects.inventoryId)));
    }
    return report;
  };
  return { accept: () => { pending = false; positions.accept(); }, refuse: refusal => correct(refusal, () => {}), correct };
}

const latestFloorWrites = new Map<string, string>();
function ownsFloorPosition(encounterId: string, kind: 'inventory' | 'creature', id: string, op: string): boolean {
  const latest = latestFloorWrites.get(`${encounterId}:${kind}:${upper(id)}`);
  return latest === undefined || latest === upper(op);
}
/** One in-flight position write, resolved by identity on failure. No row snapshot, diff, or history stack. */
function drawFloorPositions(encounterId: string, operationId: string, inventoryIds: string[], creatureIds: string[], mutate: () => void): FloorDraw {
  const facts: Array<{ id: string; kind: 'inventory' | 'creature' }> = [...new Set(inventoryIds)].map(id => ({ id: upper(id), kind: 'inventory' }));
  facts.push(...[...new Set(creatureIds)].map(id => ({ id: upper(id), kind: 'creature' as const })));
  const find = (id: string, kind: 'inventory' | 'creature'): Row | null => {
    const current = actorEncounterFor(encounterId) as Row | null;
    return kind === 'creature' ? current && creatureRow(current, id)
      : (current?.encounter_inventories_arr as Row[] | undefined)?.find(row => inventoryId(row) === id) ?? null;
  };
  const writes = facts.flatMap(fact => {
    const row = find(fact.id, fact.kind);
    if (!row) return [];
    const key = `${encounterId}:${fact.kind}:${fact.id}`;
    const previousOwner = latestFloorWrites.get(key);
    latestFloorWrites.set(key, upper(operationId));
    return [{ ...fact, key, previousOwner, row, before: position(row), after: position(row), rev: 0 }];
  });
  mutate();
  for (const write of writes) {
    const row = find(write.id, write.kind) ?? write.row;
    if (row) {
      write.after = position(row);
      write.rev = write.kind === 'creature' ? Number(ftEncounterCreatureField(row.encounter_creatures, 'movement_rev') ?? 0) : 0;
    }
  }
  let pending = true;
  const correct = (_refusal: TruthRefusal, applySaved: () => void): UndoReport | null => {
      if (!pending) return null;
      pending = false;
      const report: UndoReport = { reverted: 0, kept: 0, conflicts: 0, conflictPaths: [] };
      for (const write of writes) {
        const row = find(write.id, write.kind), now = row ? position(row) : null;
        const rev = write.kind === 'creature' && row ? Number(ftEncounterCreatureField(row.encounter_creatures, 'movement_rev') ?? 0) : 0;
        if (latestFloorWrites.get(write.key) !== upper(operationId) || !now || rev !== write.rev
          || now.x !== write.after.x || now.y !== write.after.y || now.z !== write.after.z) { report.conflicts++; continue; }
        if (write.kind === 'inventory') setPosition(encounterId, write.id, write.before, 'inventory_floor_move_reverted');
        else setCreaturePosition(encounterId, write.id, write.before, 'inventory_floor_move_reverted');
        report.reverted++;
      }
      applySaved();
      for (const write of writes) {
        const row = find(write.id, write.kind), now = row ? position(row) : null;
        if (latestFloorWrites.get(write.key) !== upper(operationId) || !now
          || now.x !== write.before.x || now.y !== write.before.y || now.z !== write.before.z) continue;
        if (write.previousOwner === undefined) latestFloorWrites.delete(write.key);
        else latestFloorWrites.set(write.key, write.previousOwner);
      }
      // Each position setter publishes its own compact delta.
      window.dispatchEvent(new CustomEvent('ft:inventory-floor-move-undone', { detail: { operationId, conflicts: report.conflicts } }));
      return report;
  };
  return { accept: () => { pending = false; }, refuse: refusal => correct(refusal, () => {}), correct };
}
const finitePos = (q: { x?: unknown; y?: unknown; z?: unknown } | null | undefined): q is Pos =>
  !!q && [q.x, q.y, q.z].every(n => typeof n === 'number' && Number.isFinite(n));
export function applyCreatureFollower(encounterId: string, follower: CreatureFollowerFrame, subType: string): void {
  const encounter = actorEncounterFor(encounterId) as Row | null, row = encounter ? creatureRow(encounter, upper(follower.id)) : null;
  if (!row || !finitePos(follower)) return;
  const current = Number(ftEncounterCreatureField(row.encounter_creatures, 'movement_rev') ?? 0);
  if (follower.rev !== undefined && follower.rev < current) return;
  setCreaturePosition(encounterId, upper(follower.id), follower, subType);
  if (follower.rev !== undefined && asRow(row.encounter_creatures)) {
    (row.encounter_creatures as Row).movement_rev = follower.rev;
    bankLegacyRowTruth(encounterId, upper(follower.id), row as never, 'commit');
  }
}
/** A provisional / committed frame's draw on a peer (idempotent). */
function applyFloorFrame(command: InventoryFloorMoveWireFrame, kind: 'provisional' | 'committed'): void {
  const eid = upper(command.encounter_id), iid = upper(command.inventory_id);
  if (Object.prototype.hasOwnProperty.call(command, 'transform')) {
    setTransformById(eid, iid, command.transform ?? null, 'inventory_transform_' + kind);
    if (command.resize) setResize(eid, iid, command.resize, 'inventory_transform_' + kind);
  }
  if (command.fall_damage) {
    const enc = actorEncounterFor(eid) as Row | null;
    if (command.fall_damage.saved) applySavedFall(eid, command.fall_damage.saved);
    else if (command.fall_damage.frame && enc) { applyObjectDestruction(enc, command.fall_damage.frame); rerender(eid, enc, 'inventory_fall_destroyed_' + kind); }
    else if (typeof command.fall_damage.v_current === 'number') setVitalityById(eid, iid, command.fall_damage.v_current, Number(command.fall_damage.v_temp ?? 0), 'inventory_fall_damage_' + kind);
  }
  for (const f of Array.isArray(command.followers) ? command.followers : []) if (finitePos(f)) setPosition(eid, upper(f.id), f, 'inventory_floor_move_' + kind + '_follower');
  if (command.corpse && finitePos(command.corpse)) setCreaturePosition(eid, upper(command.corpse.encounter_creature_id), command.corpse, 'inventory_floor_move_' + kind + '_corpse');
  for (const follower of command.creature_followers ?? []) applyCreatureFollower(eid, follower, 'inventory_floor_move_' + kind + '_team');
  if (finitePos(command.position)) setPosition(eid, iid, command.position, `inventory_floor_move_${kind}`);
}
/** The receipt and committed frame carry the actual raw saved rows; only the browser builds their display frame. */
function applySavedFall(encounterId: string, rows: unknown): void {
  if (!Array.isArray(rows)) return;
  const encounter = actorEncounterFor(encounterId) as Row | null;
  if (!encounter) return;
  const removed: string[] = [], added: ObjectDestroyFrame['add'] = [];
  for (const raw of rows) {
    if (!asRow(raw)) continue;
    const node = { inventory: raw }, id = upper(readInventoryField(node, 'inventory_id'));
    if (Number(readInventoryField(node, 'inventory_deleted') ?? 0) !== 0) { removed.push(id); continue; }
    const point = { x_pos: Number(readInventoryField(node, 'x_pos') ?? 0), y_pos: Number(readInventoryField(node, 'y_pos') ?? 0), z_pos: Number(readInventoryField(node, 'z_pos') ?? 0) };
    const roster = readInventoryRows(encounter, 'encounter_inventories_arr'), current = roster.find(row => inventoryId(row) === id);
    if (current) {
      Object.assign(inv(current), raw, { transform_3d: normalizeInventoryTransform(readInventoryField(node, 'transform_3d')) });
      current.position = point;
      const vitality = readInventoryField(node, 'v_current');
      if (vitality !== null) setVitalityById(encounterId, id, Number(vitality), Number(readInventoryField(node, 'v_temp') ?? 0), 'inventory_fall_saved');
    } else added.push({ inventory: { ...raw, transform_3d: normalizeInventoryTransform(readInventoryField(node, 'transform_3d')) },
      position: point,
      permissions: { player: true, campaign: true }, computed: { possessor: { type: 'encounter' } } } as ObjectDestroyFrame['add'][number]);
  }
  applyObjectDestruction(encounter, { remove: removed, add: added });
  rerender(encounterId, encounter, 'inventory_fall_saved');
}
/** A correction's saved spots (after the provisional draw was reverted, or when this page never drew it): the row gone, or back where it
 * is saved with its pin / size / followers / body. */
function applyFloorCorrection(command: InventoryFloorMoveWireFrame, tracked = false): void {
  const eid = upper(command.encounter_id), iid = upper(command.inventory_id);
  const current = !tracked || ownsFloorPosition(eid, 'inventory', iid, command.operation_id);
  if (!command.restore) {
    if (current) removeRow(eid, iid, 'inventory_floor_move_gone');
    if (!tracked) latestFloorWrites.set(`${eid}:inventory:${iid}`, upper(command.operation_id));
    return;
  }
  // A correction for an older operation must not overwrite a newer pending/saved move of the same member.
  if (current && finitePos(command.restore)) bankSavedFloorSpot(eid, iid, command.restore, {
    ...(Object.prototype.hasOwnProperty.call(command, 'restore_transform') ? { transform_3d: command.restore_transform } : {}),
    ...command.restore_resize,
  });
  if (current && Object.prototype.hasOwnProperty.call(command, 'restore_transform')) setTransformById(eid, iid, command.restore_transform, 'inventory_floor_move_corrected_transform');
  if (current && command.restore_resize) setResize(eid, iid, command.restore_resize, 'inventory_floor_move_corrected_resize');
  for (const f of Array.isArray(command.restore_followers) ? command.restore_followers : []) if (finitePos(f)) {
    if (tracked && !ownsFloorPosition(eid, 'inventory', f.id, command.operation_id)) continue;
    bankSavedFloorSpot(eid, upper(f.id), f);
    setPosition(eid, upper(f.id), f, 'inventory_floor_move_corrected_follower');
  }
  for (const follower of command.restore_creature_followers ?? []) if (!tracked || ownsFloorPosition(eid, 'creature', follower.id, command.operation_id))
    applyCreatureFollower(eid, follower, 'inventory_floor_move_corrected_team');
  const rc = command.restore_corpse;
  if (rc && finitePos(rc) && (!tracked || ownsFloorPosition(eid, 'creature', rc.encounter_creature_id, command.operation_id))) setCreaturePosition(eid, upper(rc.encounter_creature_id), rc, 'inventory_floor_move_corrected_corpse');
  if (current && finitePos(command.restore)) setPosition(eid, iid, command.restore, 'inventory_floor_move_correction');
  if (!tracked) {
    for (const id of [iid, ...(command.restore_followers ?? []).map(member => upper(member.id))]) latestFloorWrites.set(`${eid}:inventory:${id}`, upper(command.operation_id));
    for (const id of [...(command.restore_creature_followers ?? []).map(member => upper(member.id)), ...(rc ? [upper(rc.encounter_creature_id)] : [])])
      latestFloorWrites.set(`${eid}:creature:${id}`, upper(command.operation_id));
  }
}
/** Pending saves only; settled operations retain no before-values. */
const peerDraws = new RevertLedger<InventoryFloorMoveWireFrame>();
/** Peers apply the same touched-row position delta as the writer; fall/transform effects retain scoped recovery. */
export function handleInventoryFloorMoveTruth(command: InventoryFloorMoveWireFrame): void {
  if (command?.schema !== FLOOR_MOVE_SCHEMA) return;
  const kind = command.correction ? 'correction' : command.committed ? 'committed' : 'provisional';
  if (kind === 'committed') refreshSavedInventory(command.base_refresh);
  if (own.has(command.operation_id)) return;
  if (!noteSeen(command.operation_id, kind)) return;
  const eid = upper(command.encounter_id), op = upper(command.operation_id);
  if (kind === 'correction') {
    // the frame itself is the authoritative payload (restore / restore_transform / restore_resize / restore_followers / restore_corpse)
    peerDraws.correction(op, { ...command, authoritative: command }, () => applyFloorCorrection(command));
    return;
  }
  if (kind === 'committed') {
    peerDraws.accept(op);
    if (command.applied_frame) retireTransferredInventory(command.applied_frame);
    applyFloorFrame(command, kind);
    if (command.manipulation_receipt) (window as Window & { ftObjectManipulationTruth?: ObjectManipulationBridge })
      .ftObjectManipulationTruth?.applyReceipt(command.manipulation_receipt);
    if (command.movement_receipt && command.before) { const preview = {
      type: 'ft3d_move_preview' as const, schema: 'ft.ft3d.authoritative-move-commit.v1' as const, move_id: op.toLowerCase(),
      encounter_id: eid, from_user_id: 'inventory-truth', kind: 'inventory' as const, draggable_id: command.inventory_id,
      origin: { x: command.before.x, z: command.before.y, elevation: command.before.z },
      preview: { x: Number(command.position.x), z: Number(command.position.y), elevation: Number(command.position.z) },
      accepted_path: command.movement_receipt.accepted_path, committed: true,
      speed_variable: command.movement_receipt.speed_variable,
      origin_client_instance_id: command.client_instance_id, origin_sequence: command.sequence,
    }; handleFt3dMovePreview(preview); }
    return;
  }
  if (!peerDraws.admit(op)) return;
  const inventoryIds = [upper(command.inventory_id), ...(command.followers ?? []).map(member => upper(member.id))];
  const companionIds = (command.creature_followers ?? []).map(member => upper(member.id));
  const draw = drawFloorEffects(eid, op, inventoryIds, [...companionIds, ...(command.corpse ? [upper(command.corpse.encounter_creature_id)] : [])], {
    inventoryId: upper(command.inventory_id), transform: Object.prototype.hasOwnProperty.call(command, 'transform'),
    resize: command.resize, damage: command.fall_damage,
  }, () => applyFloorFrame(command, 'provisional'));
  peerDraws.track(op, { accept: draw.accept, refuse: refusal => draw.correct!(refusal, () => {
    if (asRow(refusal.authoritative)) applyFloorCorrection(refusal.authoritative as InventoryFloorMoveWireFrame, true);
  }) });
}


const numOrNull = (v: unknown): number | null => v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v);
/** Shared inherit-aware vitality for floor falls, roof overlays and carried drops. */
function rowVitality(encounter: Row, row: Row, key: 'v_max' | 'v_current' | 'v_temp' | 'effective_v_max'): number | null {
  return hydratedVitality(encounter, row)[key];
}
function hydratedVitality(encounter: Row, row: Row): { v_max: number | null; v_current: number | null; v_temp: number; effective_v_max: number | null } {
  const value = resolveObjectVitality(row, encounter, () => definitionFor(encounter, row));
  return { v_max: numOrNull(value?.v_max), v_current: numOrNull(value?.v_current),
    v_temp: numOrNull(value?.v_temp) ?? 0, effective_v_max: numOrNull(value?.effective_v_max) };
}

type ResizeFrame = { inventory_size: string | null; v_max?: number | null; v_current?: number | null };
/** A resize's size band + vitality on the roster row (the typed delta converges the compiled rest). An absent vitality key
 * means unchanged; a present one (null included) is the saved value. */
function setResize(encounterId: string, id: string, resize: ResizeFrame, subType: string): void {
  const encounter = actorEncounterFor(encounterId) as Row | null;
  const roster = Array.isArray(encounter?.encounter_inventories_arr) ? encounter!.encounter_inventories_arr as Row[] : null;
  const row = roster?.find(r => inventoryId(r) === id);
  if (!encounter || !row) return;
  const i = inv(row);
  if (resize.inventory_size === null) delete i.inventory_size; else i.inventory_size = resize.inventory_size;
  if (resize.v_max !== undefined) i.v_max = resize.v_max;
  if (resize.v_current !== undefined) i.v_current = resize.v_current;
  rerender(encounterId, encounter, subType, [row]);
}

// ---------------------------------------------------------------------------------------------------------------
// H2 — rotation / uniform scale / axis resize (the Edit Scene transform writer), decided here, saved by gesture
// 'transform' on the same endpoint. Replaces options/api/ft3d_inventory_rotation_inc.php for play-view edits.
// ---------------------------------------------------------------------------------------------------------------
export interface InventoryTransformRequest {
  encounterId: string; inventoryId: string;
  rotationDegrees?: number; scale?: number; scaleXYZ?: number[];
  expectedRotationDegrees?: number | null; expectedRowRevision?: string | null;
  /** The placement's mesh id (the world look the scene renders). */
  meshId?: string | null;
  shape?: unknown;
}
export interface InventoryTransformResult {
  ok: true; rotationDegrees: number; scale: number; scaleXYZ: number[]; positionFt: Pos; mounted: boolean;
  rowRevision?: string; inventorySize: string | null; vitality: { maximum: number | null; current: number | null } | null; rest: string;
}

/** Null reports an unavailable plan. A refusal or stale edit throws so the caller restores its placement. */
export async function tryCommitInventoryTransform(request: InventoryTransformRequest): Promise<InventoryTransformResult | null> {
  const startedAt = performance.now();
  const encounterId = upper(request.encounterId), id = upper(request.inventoryId);
  if (!UUID.test(encounterId) || !UUID.test(id)) return null;
  const preview = actorEncounterFor(encounterId) as Row | null;
  if (preview) await hydrateInventoryDefinitions(readInventoryRows(preview, 'encounter_inventories_arr'), preview.catalog, fetchAndCacheObject);
  const encounter = actorEncounterFor(encounterId) as Row | null;
  const roster = Array.isArray(encounter?.encounter_inventories_arr) ? encounter!.encounter_inventories_arr as Row[] : null;
  const row = roster?.find(r => inventoryId(r) === id) ?? null;
  if (!encounter || !roster || !row) return null;
  const rawSaved = savedFloorInventory(encounter, row);
  const i = inv(row);
  if (Number(i.inventory_deleted ?? 0) !== 0) return null;
  let verdict: ReturnType<typeof resolveTransformEdit>;
  const before = position(row);
  try {
    const { shape, depthAuthored, overlayUnknown } = encounterShape(encounter, roster, request.shape);
    if (depthAuthored && !shape) return null;
    const world: RestWorld = { shape, depthAuthored, rows: roster.map(r => restRow(encounter, r)) };
    const def = definitionFor(encounter, row);
    const tv = asRow(asRow(def?.computed)?.vitality);
    const templateMax = Number(tv?.base_v_max ?? tv?.v_max ?? NaN);
    const edit: TransformEdit = {};
    if (request.rotationDegrees !== undefined) edit.rotationDegrees = Number(request.rotationDegrees);
    if (request.scale !== undefined) edit.scale = Number(request.scale);
    if (request.scaleXYZ !== undefined) edit.scaleXYZ = request.scaleXYZ.map(Number);
    const selected = (() => {
      try {
        const set = selectInventoryMeshSet({ ...row, catalog: catalogOf(encounter, row) }, { role: 'world', bodyProfile: 'any', quality: 'automatic', entity: row });
        const mesh = set?.meshes?.length === 1 ? set.meshes[0] : null;
        return mesh?.id ? { id: String(mesh.id), boundsM: worldMeshSpace(encounter, row)?.boundsM ?? null } : null;
      } catch { return null; }
    })();
    const input = footprintInputs(encounter, row);
    verdict = resolveTransformEdit({
      current: i.transform_3d ?? null, edit, expectedRotationDegrees: request.expectedRotationDegrees ?? null,
      position: position(row), vitality: { vMax: rowVitality(encounter, row, 'v_max'), vCurrent: rowVitality(encounter, row, 'v_current') },
      expectedMeshId: request.meshId ?? null, worldMesh: selected,
      template: { sizeId: input.templateSizeId, vMax: Number.isFinite(templateMax) ? templateMax : null },
      shapeOverlayUnknown: overlayUnknown,
    }, world, (transform, inventorySize) => {
      const probe: Row = { ...row, inventory: { ...i, transform_3d: transform, ...(inventorySize ? { inventory_size: inventorySize } : {}) } };
      const fin = footprintInputs(encounter, probe);
      return { id, mounted: normalizeTransform(transform)?.mounted === true, integrated: statusIds(row).includes(INTEGRATED_OBJECT_STATUS),
        footprintAt: (x, y) => inventoryFootprint(fin, sizeBase, { x, y }), height: inventoryHeightFt(fin, sizeBase) };
    });
  } catch (error) {
    window.dispatchEvent(new CustomEvent('ft:inventory-transform-slice-unavailable', { detail: { reason: String(error), inventoryId: id } }));
    return null;
  }
  if (verdict.verdict === 'handBack') {
    window.dispatchEvent(new CustomEvent('ft:inventory-transform-slice-unavailable', { detail: { reason: verdict.reason, inventoryId: id } }));
    return null;
  }
  if (verdict.verdict === 'refuse') throw new Error(verdict.error);
  const operationId = crypto.randomUUID().toUpperCase();
  const resize: ResizeFrame | null = verdict.inventorySize
    ? { inventory_size: verdict.inventorySize, ...(verdict.vMax !== undefined ? { v_max: verdict.vMax, v_current: verdict.vCurrent ?? null } : {}) } : null;
  if (resize && !rawSaved) return null;
  // A turn / resize that drops the object carries its linked rows the same distance.
  const moved = Math.abs(verdict.after.z - before.z) > 1e-9;
  const linked = moved ? await linkedRows(encounter, roster, row, id, before, verdict.after) : { followers: [], corpse: null };
  if (!linked) {
    window.dispatchEvent(new CustomEvent('ft:inventory-transform-slice-unavailable', { detail: { reason: 'lock-bindings-unreadable', inventoryId: id } }));
    return null;
  }
  if (verdict.fallFt > 5) await prefetchCompositions(encounter, row);
  const fall = planFallDamage(encounter, row, id, verdict.fallFt, verdict.after, resize, rawSaved);
  if (fall.kind === 'handBack') {
    window.dispatchEvent(new CustomEvent('ft:inventory-transform-slice-unavailable', { detail: { reason: 'fall-' + fall.reason, inventoryId: id } }));
    return null;
  }
  const probe: Row = { ...row, inventory: { ...i, transform_3d: verdict.transform, ...(verdict.inventorySize ? { inventory_size: verdict.inventorySize } : {}) } };
  const team = moved ? planFloorCreatureFollowers(encounter, probe, verdict.after,
    new Set([...linked.followers.map(member => member.id), ...(linked.corpse ? [linked.corpse.encounter_creature_id] : [])])) : { members: [], reason: null };
  if (team.reason !== null) throw new Error(`The latched team cannot land there: ${team.reason}`);
  const envelope = {
    schema: team.members.length ? COMPOUND_FLOOR_MOVE_SCHEMA : FLOOR_MOVE_SCHEMA, operation_id: operationId, encounter_id: encounterId, inventory_id: id, actor_id: null,
    ...(team.members.length ? { creature_followers: team.members } : {}),
    before: { possess_id: upper(i.possess_id) || encounterId, ...before, qty: Number(i.item_qty ?? 1), deleted: 0, transform: i.transform_3d ?? null,
      ...(resize ? { inventory_size: uuidOf(readInventoryField(rawSaved, 'inventory_size')), v_max: savedFloorVitality(rawSaved!).v_max, v_current: savedFloorVitality(rawSaved!).v_current } : {}) },
    after: { ...verdict.after, transform: verdict.transform, ...(resize ?? {}) },
    gesture: 'transform', rest: verdict.rest, fall_ft: verdict.fallFt, followers: linked.followers, corpse: linked.corpse, ft_moved: null,
    client_instance_id: '', sequence: 0, expected_row_revision: request.expectedRowRevision ?? null,
    movement_receipt: floorMovementReceipt(before, verdict.after),
    ...(fall.kind === 'damage' ? { fall_damage: fall.envelope } : {}),
  };
  own.add(operationId);
  if (own.size > 256) own.delete(own.values().next().value!);
  latestOp.set(id, operationId);
  setTransformById(encounterId, id, verdict.transform, 'inventory_transform_local');
  setPosition(encounterId, id, verdict.after, 'inventory_transform_local');
  pushFloorMoveTruth(envelope);
  let response: Response | null = null;
  let result: Row | null = null;
  for (let attempt = 1; attempt <= RETRIES; attempt++) {
    try {
      response = await saveAppliedTruth(ENDPOINT, envelope, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(envelope), signal: AbortSignal.timeout(20000) });
      try { result = await response.json() as Row; } catch { result = null; }
      if (response.status < 500) break;
    } catch { response = null; }
    if (attempt < RETRIES) await new Promise(r => setTimeout(r, 400 * attempt));
  }
  if (!response) throw new Error('inventory_transform_failed');
  if (!(response.ok && result?.ok === true)) {
    const error = String(result?.error ?? response.statusText ?? 'error');
    if (response.status === 409 && error === 'stale') {
      const saved = asRow(result?.saved);
      if (!saved) { if (latestOp.get(id) === operationId) removeRow(encounterId, id, 'inventory_transform_gone'); throw new Error('encounter_inventory_membership_changed'); }
      if (latestOp.get(id) === operationId) {
        setTransformById(encounterId, id, saved.transform ?? null, 'inventory_transform_saved');
        setPosition(encounterId, id, { x: Number(saved.x), y: Number(saved.y), z: Number(saved.z) }, 'inventory_transform_saved');
      }
      window.dispatchEvent(new CustomEvent('ft:inventory-floor-move-truth-refused', { detail: { envelope, message: 'stale' } }));
      throw new Error(request.expectedRotationDegrees !== undefined && request.expectedRotationDegrees !== null
        ? 'inventory_rotation_changed_since_edit' : 'inventory_changed_since_edit');
    }
    if (response.status === 403) throw new Error('gm_access_required');
    throw new Error(response.status >= 500 ? 'inventory_transform_failed' : error);
  }
  // Saved: the roster row takes the answer (the typed delta converges the compiled row for everyone).
  if (latestOp.get(id) === operationId) {
    setTransformById(encounterId, id, verdict.transform, 'inventory_transform_local');
    if (resize) setResize(encounterId, id, resize, 'inventory_transform_local');
    setPosition(encounterId, id, verdict.after, 'inventory_transform_local');
    for (const f of linked.followers) setPosition(encounterId, f.id, f.after, 'inventory_transform_follower');
    if (linked.corpse) setCreaturePosition(encounterId, linked.corpse.encounter_creature_id, verdict.after, 'inventory_transform_corpse');
    for (const member of Array.isArray(result.creature_followers) ? result.creature_followers as CreatureFollowerFrame[] : []) applyCreatureFollower(encounterId, member, 'inventory_transform_team');
    if (fall.kind === 'damage') fall.apply();
  }
  applySavedFall(encounterId, asRow(result.fall_damage)?.saved);
  if (result.base_refresh) await window.ftApplyBasePatch?.(result.base_refresh as BasePatchFrame).catch(error => console.error('[inventory-floor-move] saved base refresh', error));
  window.dispatchEvent(new CustomEvent('ft:inventory-floor-move-truth-committed', { detail: { envelope, result, ms: Math.round((performance.now() - startedAt) * 10) / 10 } }));
  const saved = normalizeTransform(verdict.transform);
  return {
    ok: true, rotationDegrees: saved?.rotationDegrees ?? 0, scale: saved?.scale ?? 1, scaleXYZ: saved?.scaleXYZ ?? [1, 1, 1],
    positionFt: { ...verdict.after }, mounted: saved?.mounted === true,
    ...(typeof result.row_revision === 'string' ? { rowRevision: result.row_revision } : {}),
    inventorySize: resize?.inventory_size ?? null,
    vitality: resize && resize.v_max !== undefined ? { maximum: resize.v_max ?? null, current: resize.v_current ?? null } : null, rest: verdict.rest,
  };
}

Object.assign(window, { ftTryCommitFloorInventoryMove: tryCommitFloorInventoryMove, ftTryCommitInventoryTransform: tryCommitInventoryTransform,
  ftApplyTransportInventoryTruth: handleInventoryFloorMoveTruth });

// Bundle bridge: the scene uses the websocket bundle's held world, never its module state. The planner stays synchronous; the
// caller awaits ftFloorCarriedIds(encounterId) first and passes the floor's carried set (null / omitted = dead rows stay uncertain).
Object.assign(window, { ftPlanPartyRelocation: (campaign: Row, encounter: Row, shape: unknown, roomId: string, carriedIds: ReadonlySet<string> | null = null) => {
  const built = routeLiftWorld(encounter, carriedIds);
  if (built.overlayUnknown && !shape) throw new Error('party-relocation:geometry-overlay-unavailable');
  return planPartyRelocation(campaign, encounter, shape, roomId, built.world);
}, ftFloorCarriedIds: freshCarriedIds });

Object.assign(window, { ftDoorLockState: async (encounterId: string, hostId: string, rows: Row[]) => {
  const links = await floorLinks(encounterId, true);
  return doorLockState(hostId, rows, links.rows, (row, side) => (ftCreatureStatuses(row, side) ?? []) as Row[]);
} });

export { floorLinks as geometryDoorBindings, freshCarriedIds };

export function planHeldInventoryPlacement(encounter: Row, row: Row, world: RestWorld, point: Pos): ReturnType<typeof planInventoryPlacement> {
  const footprint = footprintInputs(encounter, row);
  return planInventoryPlacement(world, { id: inventoryId(row), mounted: normalizeTransform(inv(row).transform_3d)?.mounted === true,
    integrated: statusIds(row).includes(INTEGRATED_OBJECT_STATUS), height: inventoryHeightFt(footprint, sizeBase),
    footprintAt: (x, y) => inventoryFootprint(footprint, sizeBase, { x, y }) }, point);
}

/** Hydrate only missing placement inputs; all callers share the floor's support/mesh readers. */
async function newInventoryPlacementInputs(request: Row): Promise<InventorySpawnPlacement> {
  const eid = upper(request.encounter_id || request.location_id);
  const encounter = heldEncounterForAction(eid) ?? await fetchAndCacheObject(eid, { fresh: true }) as Row | null;
  if (!encounter) throw new Error('placement:encounter-unavailable');
  if (!Array.isArray(encounter.encounter_inventories_arr)) await window.ftAdoptInventoryBase?.(encounter, { transient: true });
  const itemId = upper(request.inventory_item_id ?? request.item_id);
  const definition = await fetchAndCacheObject(itemId) as Row | null;
  if (!definition) throw new Error('placement:item-unavailable');
  const point = asRow(request.position_ft) ?? { x: request.x_pos ?? 0, y: request.y_pos ?? 0, z: request.z_pos ?? 0 };
  const transform = normalizeTransform(request.transform_3d ?? { rotationDegrees: request.rotation_degrees ?? 0,
    scale: request.scale ?? 1, mounted: request.mounted === true });
  const row: Row = { ...definition, catalog: { ...asRow(encounter.catalog), [itemId]: definition },
    inventory: { inventory_item_id: itemId, item_qty: Number(request.item_qty ?? 1),
      mesh_id: request.mesh_id ?? null, transform_3d: transform },
    position: { x_pos: Number(point.x), y_pos: Number(point.y), z_pos: Number(point.z) } };
  const rows = readInventoryRows(encounter, 'encounter_inventories_arr');
  await hydrateInventoryDefinitions(rows, encounter.catalog, fetchAndCacheObject);
  const geometry = encounterShape(encounter, rows, request.shape);
  const world: RestWorld = { shape: geometry.shape, depthAuthored: geometry.depthAuthored,
    rows: rows.map(r => restRow(encounter, r)) };
  const footprint = footprintInputs(encounter, row);
  return { world, transform, mover: { id: '', mounted: transform?.mounted === true,
    integrated: statusIds(row).includes(INTEGRATED_OBJECT_STATUS), height: inventoryHeightFt(footprint, sizeBase),
    footprintAt: (x, y) => inventoryFootprint(footprint, sizeBase, { x, y }) } };
}
/** Bundle bridge for exact scene/drop points; plain births use the same inputs in their source planner. */
async function planNewInventoryPlacement(request: Row): Promise<Row> {
  const { world, mover, transform } = await newInventoryPlacementInputs(request);
  const point = asRow(request.position_ft) ?? { x: request.x_pos ?? 0, y: request.y_pos ?? 0, z: request.z_pos ?? 0 };
  const result = planInventoryPlacement(world, mover, { x: Number(point.x), y: Number(point.y), z: Number(point.z) });
  return { x_pos: result.position.x, y_pos: result.position.y, z_pos: result.position.z,
    position_ft: result.position, mounted: result.mounted,
    transform_3d: JSON.stringify({ ...transform, schema: 'ft.inventory-transform-3d.v1', mounted: result.mounted }) };
}
Object.assign(window, { ftPlanNewInventoryPlacement: planNewInventoryPlacement, ftNewInventoryPlacementInputs: newInventoryPlacementInputs });
